'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import {
 mockGames, sportFilters, currentUser,
 formatDistance, formatTime, getTimeUntil,
 getStatusPillClass, getStatusLabel,
 type MockGame, type MockPlayer,
} from '@/lib/mock-data';
import {
  fetchNearbyGames,
  joinGameApi,
  leaveGameApi,
  createGameApi,
  checkInApi,
  fetchEventMessagesApi,
  sendEventMessageApi,
  fetchUserJoinedEventsApi,
  fetchNotificationsApi,
  markNotificationsReadApi,
  getGameSocket,
  generateIdempotencyKey,
  syncUserApi,
  reverseGeocodeApi,
  getNearestDistrictsApi,
  fetchGameApi,
  recordConsentApi,
  type District,
} from '@/lib/api';
import AuthModal from '@/components/AuthModal';
import LocationModal from '@/components/LocationModal';
import DistrictSelector from '@/components/DistrictSelector';
import { requestNotificationPermission } from '@/lib/permissions';
import { createClient } from '@/utils/supabase/client';

import { getGoogleMapsUrl, getNearbyVenues, POPULAR_VENUES, calculateDistanceMeters } from '@/lib/maps';

const GameMap = dynamic(() => import('@/components/GameMap'), { ssr: false });
const LocationPickerMap = dynamic(() => import('@/components/LocationPickerMap'), { ssr: false });
import { SportCardBanner, SportAvatarBadge } from '@/components/SportHeroArt';
import { SportIcon } from '@/components/SportIcon';
import FindGamesPage from '@/components/FindGamesPage';
import KhelDropLogo from '@/components/KhelDropLogo';
import BottomNav from '@/components/BottomNav';
import GameGuideModal from '@/components/GameGuideModal';
import { ElasticSearchEngine } from '@/lib/search-engine';
import SEOHead from '@/components/SEOHead';


const Icons = {
 Home: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
 Search: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
 Plus: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
 Calendar: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
 MessageSquare: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
 Bell: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>,
 User: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
 MapPin: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>,
 Filter: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
 ChevronRight: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
 ArrowRight: ({ style }: { style?: React.CSSProperties } = {}) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', display: 'inline-block', verticalAlign: 'middle', ...style }}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
 Settings: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/></svg>,
 Trophy: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 010-5H6"/><path d="M18 9h1.5a2.5 2.5 0 000-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22"/><path d="M18 2H6v7a6 6 0 0012 0V2z"/></svg>,
 Map: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>,
 Logo: ({ size = 28, color = '#7C3AED' }: { size?: number | string; color?: string } = {}) => (
    <KhelDropLogo size={size} color={color} />
  ),
 X: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Menu: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
  Compass: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
  ExternalLink: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>,
  Lock: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>,
  Flame: ({ size = 16, style }: { size?: number; style?: React.CSSProperties } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>
  ),
  Zap: ({ size = 16, style }: { size?: number; style?: React.CSSProperties } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
  ),
  Crown: ({ size = 16, style }: { size?: number; style?: React.CSSProperties } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}><path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"/></svg>
  ),
  Shield: ({ size = 16, style }: { size?: number; style?: React.CSSProperties } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
  ),
  AlertTriangle: ({ size = 16, style }: { size?: number; style?: React.CSSProperties } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
  ),
  Clock: ({ size = 16, style }: { size?: number; style?: React.CSSProperties } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
  ),
  Hourglass: ({ size = 16, style }: { size?: number; style?: React.CSSProperties } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}><path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/></svg>
  ),
  Flag: ({ size = 16, style }: { size?: number; style?: React.CSSProperties } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
  ),
  Ban: ({ size = 16, style }: { size?: number; style?: React.CSSProperties } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
  ),
  Check: ({ size = 16, style }: { size?: number; style?: React.CSSProperties } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}><polyline points="20 6 9 17 4 12"/></svg>
  ),
  Siren: ({ size = 16, style }: { size?: number; style?: React.CSSProperties } = {}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}><path d="M7 18v-6a5 5 0 1 1 10 0v6"/><path d="M5 21a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-1a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2z"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="4.93" y1="4.93" x2="6.34" y2="6.34"/><line x1="19.07" y1="4.93" x2="17.66" y2="6.34"/></svg>
  ),
};

export const isUserHostOfGame = (game?: MockGame | null, user?: any): boolean => {
  if (!user || !game) return false;
  const uId = user.id || user._id;
  if (uId && game.hostId && (game.hostId === uId || (game.hostId as any)?._id === uId || game.hostId.toString() === uId.toString())) return true;
  const userFullName = (user.user_metadata?.full_name || user.name || '').toLowerCase();
  if (userFullName && game.hostName && game.hostName.toLowerCase() === userFullName) return true;
  const userPrefix = user.email ? user.email.split('@')[0].toLowerCase() : '';
  if (userPrefix && game.hostName && game.hostName.toLowerCase() === userPrefix) return true;
  if (game.players?.some(p => p.isHost && (p.id === uId || (userPrefix && p.name.toLowerCase().includes(userPrefix)) || (userFullName && p.name.toLowerCase() === userFullName)))) return true;
  return false;
};

export const isUserPlayerInGame = (game?: MockGame | null, user?: any, joinedSet?: Set<string>): boolean => {
  if (!user || !game) return false;
  if (joinedSet && joinedSet.has(game.id)) return true;
  if (isUserHostOfGame(game, user)) return true;
  const uId = user.id || user._id;
  const userPrefix = user.email ? user.email.split('@')[0].toLowerCase() : '';
  return Boolean(game.players?.some(p => p.id === uId || (userPrefix && (p.name.toLowerCase().includes(userPrefix) || p.id === userPrefix))));
};

export default function HomePage() {
 const [activeSport, setActiveSport] = useState('ALL');
 const [searchQuery, setSearchQuery] = useState('');
 const [showSearchFilters, setShowSearchFilters] = useState(false);
 const [maxFilterDistanceKm, setMaxFilterDistanceKm] = useState<number>(50);
 const [filterNeedOneOnly, setFilterNeedOneOnly] = useState(false);
 const [selectedGame, setSelectedGame] = useState<MockGame | null>(null);
 const [activeTab, setActiveTab] = useState('home');
 const [toastMessage, setToastMessage] = useState('');
 const [toastType, setToastType] = useState<'success' | 'error'>('success');
 const [showToast, setShowToast] = useState(false);
 const [games, setGames] = useState<MockGame[]>(mockGames);
 const [joinedGames, setJoinedGames] = useState<Set<string>>(new Set());
 const [liveCounters, setLiveCounters] = useState<Map<string, number>>(new Map());
 const [showCreateModal, setShowCreateModal] = useState(false);
 const [isLiveConnected, setIsLiveConnected] = useState(false);
 const [showAuthModal, setShowAuthModal] = useState(false);
 const [authUser, setAuthUser] = useState<any>(null);
 const [waiverGame, setWaiverGame] = useState<MockGame | null>(null);
 const [unreadNotifCount, setUnreadNotifCount] = useState<number>(0);
 const [isActionPending, setIsActionPending] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState<District | null>(() => {
    if (typeof window !== 'undefined') {
      const savedDistrict = localStorage.getItem('kheldrop_selected_district');
      if (savedDistrict) {
        try {
          return JSON.parse(savedDistrict);
        } catch {}
      }
    }
    return null;
  });

  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
    name: string;
    isLive: boolean;
  } | null>(() => {
    if (typeof window !== 'undefined') {
      const savedCoords = localStorage.getItem('kheldrop_live_coords');
      if (savedCoords) {
        try {
          const c = JSON.parse(savedCoords);
          if (c?.lat && c?.lng) {
            return {
              lat: c.lat,
              lng: c.lng,
              name: (c.name && !c.name.includes('Live Location (') && !(c.name.includes('(') && c.name.includes(')'))) ? c.name : 'Current Location',
              isLive: true,
            };
          }
        } catch {}
      }
      const savedDistrict = localStorage.getItem('kheldrop_selected_district');
      if (savedDistrict) {
        try {
          const d = JSON.parse(savedDistrict);
          if (d?.lat && d?.lng) {
            return {
              lat: d.lat,
              lng: d.lng,
              name: `${d.name}, ${d.state || ''}`.trim(),
              isLive: false,
            };
          }
        } catch {}
      }
    }
    return null;
  });
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [showLocationBanner, setShowLocationBanner] = useState(false);
  const [showDistrictSelector, setShowDistrictSelector] = useState(false);
  const [pendingGeoForDistrict, setPendingGeoForDistrict] = useState<{ lat: number; lng: number } | null>(null);
  const [showGuideModal, setShowGuideModal] = useState<boolean>(false);
  const [guideInitialMode, setGuideInitialMode] = useState<'host' | 'join'>('host');

  useEffect(() => {
    const handleOpenGuide = () => {
      window.dispatchEvent(new CustomEvent('kheldrop_start_tour'));
    };
    window.addEventListener('kheldrop_open_guide', handleOpenGuide);
    return () => window.removeEventListener('kheldrop_open_guide', handleOpenGuide);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  const showToastMessage = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setToastMessage(msg);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3500);
  }, []);

  const requestLiveLocation = useCallback(async (isUserInitiated = false) => {
    if (typeof window === 'undefined' || !navigator.geolocation) {
      const msg = 'Geolocation is not supported by your browser';
      setLocationError(msg);
      if (isUserInitiated) showToastMessage(msg, 'error');
      return;
    }

    setIsLocating(true);
    setLocationError('');

    const applyPosition = async (lat: number, lng: number) => {
      let areaName = 'Current Location';
      try {
        const info = await reverseGeocodeApi(lat, lng);
        if (info?.displayName) {
          areaName = info.displayName;
        }
      } catch {}

      setUserLocation({
        lat,
        lng,
        name: areaName,
        isLive: true,
      });
      setIsLocating(false);
      setShowLocationBanner(false);
      if (typeof window !== 'undefined') {
        localStorage.setItem('kheldrop_location_permission', 'granted');
        localStorage.setItem('kheldrop_live_coords', JSON.stringify({ lat, lng, name: areaName }));
      }
      showToastMessage(`Location updated: ${areaName}`);

      // Auto-associate nearest district in background without replacing user coordinates
      getNearestDistrictsApi(lat, lng, 1).then((districts) => {
        if (districts && districts.length > 0) {
          const d = districts[0];
          setSelectedDistrict(d);
          if (areaName === 'Current Location') {
            const resolved = `${d.name}, ${d.state}`;
            setUserLocation(prev => prev ? { ...prev, name: resolved } : prev);
          }
          if (typeof window !== 'undefined') {
            localStorage.setItem('kheldrop_selected_district', JSON.stringify(d));
          }
        }
      }).catch(() => {});

      fetchNearbyGames(lat, lng, 50, activeSport).then(fetched => {
        if (fetched && fetched.length > 0) {
          setGames(fetched);
          const counters = new Map<string, number>();
          fetched.forEach(g => counters.set(g.id, g.confirmedPlayers));
          setLiveCounters(counters);
        }
      }).catch(() => {});
    };

    // 2-stage GPS acquisition: fresh high accuracy -> low accuracy fallback
    navigator.geolocation.getCurrentPosition(
      (pos) => applyPosition(pos.coords.latitude, pos.coords.longitude),
      (err) => {
        console.warn('High-accuracy GPS timed out or failed, trying standard accuracy fallback...', err);
        navigator.geolocation.getCurrentPosition(
          (pos) => applyPosition(pos.coords.latitude, pos.coords.longitude),
          (fallbackErr) => {
            setIsLocating(false);
            const msg = fallbackErr.code === 1
              ? 'Location permission denied. Please allow location in your browser or search your city.'
              : 'Unable to acquire GPS signal. You can search your city or choose on map.';
            setLocationError(msg);
            if (isUserInitiated) showToastMessage(msg, 'error');
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 }
        );
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  }, [activeSport, showToastMessage]);

  useEffect(() => {
    const uid = authUser?.id || (authUser as any)?._id || authUser?.email || '';
    if (!uid) {
      setUnreadNotifCount(0);
      return;
    }
    fetchNotificationsApi(uid, selectedDistrict?.slug).then((items: any[]) => {
      if (Array.isArray(items)) {
        const unread = items.filter(n => n.isUnread).length;
        setUnreadNotifCount(unread);
      }
    }).catch(() => {});
  }, [authUser, selectedDistrict]);

  useEffect(() => {
    const socket = getGameSocket();
    if (selectedDistrict?.slug) {
      socket.emit('join-district', { districtSlug: selectedDistrict.slug, userId: authUser?.id });
    }

    const handleDistrictAlert = (payload: any) => {
      setUnreadNotifCount(prev => prev + 1);
      if (payload?.title) {
        showToastMessage(`${payload.title}: ${payload.message || 'Check notifications!'}`, 'success');
      }
    };

    socket.on('district-notification', handleDistrictAlert);
    socket.on('notification', handleDistrictAlert);

    return () => {
      socket.off('district-notification', handleDistrictAlert);
      socket.off('notification', handleDistrictAlert);
    };
  }, [selectedDistrict, authUser]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // 1. Silently enable essential cookie consent & local cache in background
      if (!localStorage.getItem('kheldrop_cookie_consent')) {
        localStorage.setItem('kheldrop_cookie_consent', 'all');
        localStorage.setItem(
          'kheldrop_cookie_settings',
          JSON.stringify({
            essential: true,
            performance: true,
            functional: true,
            matchmaking: true,
          })
        );
      }
      if ('caches' in window) {
        caches.open('kheldrop-cache-v1').catch(() => {});
      }
      if (navigator.storage && navigator.storage.persist) {
        navigator.storage.persist().catch(() => {});
      }

      // 2. Request native browser location (triggers browser prompt on left side of http)
      const locPerm = localStorage.getItem('kheldrop_location_permission');
      if (locPerm !== 'denied' && !userLocation?.isLive) {
        requestLiveLocation(false);
      }

      // 3. Request native browser notifications (triggers browser prompt on left side of http)
      const askNativeNotifications = () => {
        if ('Notification' in window && Notification.permission === 'default') {
          Notification.requestPermission().then((perm) => {
            localStorage.setItem('kheldrop_notification_permission', perm);
          }).catch(() => {});
        }
      };

      askNativeNotifications();
      window.addEventListener('click', askNativeNotifications, { once: true });
      window.addEventListener('touchstart', askNativeNotifications, { once: true });
    }
  }, [userLocation?.isLive, requestLiveLocation]);

  const handleSelectHub = useCallback((hub: { name: string; lat: number; lng: number }) => {
    setUserLocation({
      lat: hub.lat,
      lng: hub.lng,
      name: hub.name,
      isLive: true,
    });
    if (typeof window !== 'undefined') {
      localStorage.setItem('kheldrop_location_permission', 'granted');
      localStorage.setItem('kheldrop_live_coords', JSON.stringify({ lat: hub.lat, lng: hub.lng, name: hub.name }));
    }
    setShowLocationModal(false);
    showToastMessage(`Location set: ${hub.name}`);
    fetchNearbyGames(hub.lat, hub.lng, 50, activeSport).then(fetched => {
      if (fetched && fetched.length > 0) {
        setGames(fetched);
        const counters = new Map<string, number>();
        fetched.forEach(g => counters.set(g.id, g.confirmedPlayers));
        setLiveCounters(counters);
      }
    }).catch(() => {});
  }, [activeSport, showToastMessage]);

  const handleDistrictSelected = useCallback((district: District, updatedUser?: any) => {
    setSelectedDistrict(district);
    setUserLocation({
      lat: district.lat,
      lng: district.lng,
      name: `${district.name}, ${district.state}`,
      isLive: true,
    });
    setShowDistrictSelector(false);

    if (typeof window !== 'undefined') {
      localStorage.setItem('kheldrop_selected_district', JSON.stringify(district));
      localStorage.setItem('kheldrop_location_permission', 'granted');
      localStorage.setItem('kheldrop_live_coords', JSON.stringify({ lat: district.lat, lng: district.lng, name: `${district.name}, ${district.state}` }));
      const uid = updatedUser?.districtUserId || (authUser?.id ? `${authUser.id}+${district.id}` : `PLAY+${district.id}`);
      localStorage.setItem('kheldrop_district_uid', uid);
    }

    if (updatedUser) {
      setAuthUser((prev: any) => prev ? { ...prev, ...updatedUser } : updatedUser);
    }

    const distId = updatedUser?.districtUserId || (authUser?.id ? `${authUser.id.slice(-6)}+${district.id}` : `USR+${district.id}`);
    showToastMessage(`District locked: ${district.name} (${district.state}) • UID: ${distId}`, 'success');

    fetchNearbyGames(district.lat, district.lng, 50, activeSport).then(fetched => {
      if (fetched && fetched.length > 0) {
        setGames(fetched);
        const counters = new Map<string, number>();
        fetched.forEach(g => counters.set(g.id, g.confirmedPlayers));
        setLiveCounters(counters);
      }
    });
  }, [authUser, activeSport, showToastMessage]);

 // On user login: Clean demo data & ask to set location
  useEffect(() => {
    if (authUser) {
      // Clean demo games: Authenticated users only see real backend games
      const currentLat = userLocation?.lat || selectedDistrict?.lat || 28.6139;
      const currentLng = userLocation?.lng || selectedDistrict?.lng || 77.2090;
      fetchNearbyGames(currentLat, currentLng, 50, activeSport, true).then(fetched => {
        setGames(fetched || []);
        const counters = new Map<string, number>();
        (fetched || []).forEach(g => counters.set(g.id, g.confirmedPlayers));
        setLiveCounters(counters);
      });

      // Clear demo joined games
      setJoinedGames(new Set());

      // Prompt to add location if not confirmed yet
      const hasSetLocation = localStorage.getItem(`kheldrop_location_set_${authUser.id}`) ||
                             localStorage.getItem('kheldrop_location_permission') === 'granted' ||
                             authUser.districtName ||
                             authUser.district_name;
      if (!hasSetLocation) {
        setShowLocationModal(true);
      }
    } else {
      setGames(mockGames);
    }
  }, [authUser]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const magicToken = urlParams.get('magicToken');
      if (magicToken) {
        syncUserApi(magicToken).then((res: any) => {
          if (res?.user) {
            setAuthUser(res.user);
            localStorage.setItem('kheldrop_auth_user', JSON.stringify(res.user));
            localStorage.setItem('kheldrop_token', magicToken);
            showToastMessage('Logged in via Magic Link!');
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        });
      } else {
        const savedUser = localStorage.getItem('kheldrop_auth_user');
        if (savedUser) {
          try {
            setAuthUser(JSON.parse(savedUser));
          } catch {}
        }
      }
    }

    const initLat = userLocation?.lat || selectedDistrict?.lat || 28.6139;
    const initLng = userLocation?.lng || selectedDistrict?.lng || 77.2090;
    fetchNearbyGames(initLat, initLng, 50, activeSport, !!authUser).then(fetched => {
 if (fetched && fetched.length > 0) {
 setGames(fetched);
 const counters = new Map<string, number>();
 fetched.forEach(g => counters.set(g.id, g.confirmedPlayers));
 setLiveCounters(counters);
 setIsLiveConnected(true);
 }
 });

 const socket = getGameSocket();
 socket.on('connect', () => setIsLiveConnected(true));

 socket.on('game-event', (payload: any) => {
 if (payload.event === 'GAME_CREATED') {
   const activeLat = userLocation?.lat || selectedDistrict?.lat || 28.6139;
   const activeLng = userLocation?.lng || selectedDistrict?.lng || 77.2090;
   fetchNearbyGames(activeLat, activeLng, 50, activeSport).then(fetched => {
 if (fetched && fetched.length > 0) {
 setGames(fetched);
 const counters = new Map<string, number>();
 fetched.forEach(g => counters.set(g.id, g.confirmedPlayers));
 setLiveCounters(counters);
 }
 });
 showToastMessage(` New game posted: ${payload.data?.playground?.name || 'Local Ground'} (${payload.data?.sport || 'Game'})!`);
 } else if (payload.event === 'SLOT_OPENED') {
 const venue = payload.data?.venue || 'Local Turf';
 const sport = payload.data?.sport || 'Game';
 showToastMessage(` LAST MINUTE SLOT OPENED: ${venue} (${sport})! Grab it now!`, 'success');
 if (payload.data?.confirmedPlayers !== undefined) {
 setLiveCounters(prev => {
 const next = new Map(prev);
 next.set(payload.gameId, payload.data.confirmedPlayers);
 return next;
 });
 }
 } else if (payload.data?.confirmedPlayers !== undefined) {
 setLiveCounters(prev => {
 const next = new Map(prev);
 next.set(payload.gameId, payload.data.confirmedPlayers);
 return next;
 });
 }
 });


 const supabase = createClient();
 supabase.auth.getSession().then(({ data: { session } }) => {
 if (session?.user) {
 setAuthUser(session.user);
 if (typeof window !== 'undefined') {
 localStorage.setItem('kheldrop_auth_user', JSON.stringify(session.user));
 if (session.access_token) {
 localStorage.setItem('kheldrop_token', session.access_token);
 }
 }
 }
 });

 const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
 if (session?.user) {
 setAuthUser(session.user);
 if (typeof window !== 'undefined') {
 localStorage.setItem('kheldrop_auth_user', JSON.stringify(session.user));
 if (session.access_token) {
 localStorage.setItem('kheldrop_token', session.access_token);
 }
 }
 } else if (event === 'SIGNED_OUT' || !session) {
 setAuthUser(null);
 setJoinedGames(new Set());
 if (typeof window !== 'undefined') {
 localStorage.removeItem('kheldrop_auth_user');
 localStorage.removeItem('kheldrop_token');
 }
 }
 });

 return () => {
 socket.off('game-event');
 subscription.unsubscribe();
 };
 }, [activeSport, showToastMessage]);

  // Dynamically compute sport filter chips including any custom games created
  const dynamicSportFilters = useMemo(() => {
    const baseList = [
      { key: 'ALL', label: 'All', count: games.length },
      { key: 'CRICKET', label: 'Cricket', count: games.filter(g => g.sport === 'CRICKET').length },
      { key: 'FOOTBALL', label: 'Football', count: games.filter(g => g.sport === 'FOOTBALL').length },
      { key: 'BASKETBALL', label: 'Basketball', count: games.filter(g => g.sport === 'BASKETBALL').length },
      { key: 'BADMINTON', label: 'Badminton', count: games.filter(g => g.sport === 'BADMINTON').length },
      { key: 'TENNIS', label: 'Tennis', count: games.filter(g => g.sport === 'TENNIS').length },
    ];
    const knownKeys = new Set(baseList.map(b => b.key));
    const extraMap = new Map<string, number>();
    games.forEach(g => {
      if (!knownKeys.has(g.sport)) {
        extraMap.set(g.sport, (extraMap.get(g.sport) || 0) + 1);
      }
    });
    extraMap.forEach((count, sKey) => {
      const label = sKey.charAt(0) + sKey.slice(1).toLowerCase();
      baseList.push({ key: sKey, label, count });
    });
    return baseList;
  }, [games]);

  const searchEngine = useMemo(() => {
    return new ElasticSearchEngine(games);
  }, [games]);

  const { filteredGames, sortedGames } = useMemo(() => {
    const { rankedGames } = searchEngine.search(searchQuery, activeSport);
    const finalFiltered = rankedGames.filter(g => {
      const distKm = (g.distance || 0) / 1000;
      if (maxFilterDistanceKm < 50 && distKm > maxFilterDistanceKm) return false;
      if (filterNeedOneOnly) {
        const count = liveCounters.get(g.id) ?? g.confirmedPlayers;
        if (g.capacity - count !== 1) return false;
      }
      return true;
    });

    return {
      filteredGames: finalFiltered,
      sortedGames: finalFiltered,
    };
  }, [searchEngine, searchQuery, activeSport, maxFilterDistanceKm, filterNeedOneOnly, liveCounters]);

  // Automatic synchronization: Index games where current user is host or player
  useEffect(() => {
    if (!authUser || !Array.isArray(games)) return;
    const userJoinedIds = new Set<string>();
    games.forEach(g => {
      if (isUserHostOfGame(g, authUser) || isUserPlayerInGame(g, authUser)) {
        userJoinedIds.add(g.id);
      }
    });
    if (userJoinedIds.size > 0) {
      setJoinedGames(prev => {
        let changed = false;
        const next = new Set(prev);
        userJoinedIds.forEach(id => {
          if (!next.has(id)) {
            next.add(id);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [games, authUser]);

  const handleJoinGame = useCallback(async (game: MockGame, skipWaiver = false) => {
    if (!authUser) {
      setShowAuthModal(true);
      return;
    }
    if (isActionPending) return;

    // RBAC: Check if user is the host
    if (isUserHostOfGame(game, authUser)) {
      showToastMessage('You are the host of this match and already occupy slot #1.', 'error');
      return;
    }

    // Uniqueness: Check if user already joined
    if (isUserPlayerInGame(game, authUser, joinedGames)) {
      showToastMessage('You have already joined this match! One player can only fill one slot.', 'error');
      return;
    }

    // Pre-Match Volenti Non Fit Injuria & Physical Risk Waiver
    if (!skipWaiver) {
      const alreadyWaivered = typeof window !== 'undefined' ? localStorage.getItem(`kheldrop_waiver_${game.id}`) : null;
      if (!alreadyWaivered) {
        setWaiverGame(game);
        return;
      }
    }

    const currentCount = liveCounters.get(game.id) ?? game.confirmedPlayers;
    if (currentCount >= game.capacity) {
      showToastMessage('Game is full! Adding to waitlist...', 'error');
    }

    setIsActionPending(true);
    const playerId = authUser?.id || authUser?.email || "player";
    const idemKey = generateIdempotencyKey(`join_${game.id}_${playerId}`);

    try {
      const res = await joinGameApi(game.id, playerId, idemKey);

      setJoinedGames(prev => new Set([...prev, game.id]));

      const updatedConfirmed = typeof res?.confirmedPlayers === 'number'
        ? res.confirmedPlayers
        : currentCount + 1;

      setLiveCounters(prev => {
        const next = new Map(prev);
        next.set(game.id, updatedConfirmed);
        return next;
      });

      const newNeed = Math.max(0, game.capacity - updatedConfirmed);
      showToastMessage(
        res?.waitlisted
          ? `Added to waitlist! Position #${res.position || 1}`
          : newNeed === 0
          ? `Match full! All ${game.capacity} slots filled.`
          : `Joined! ${newNeed} more player${newNeed > 1 ? 's' : ''} needed.`,
        'success',
      );
    } catch (err: any) {
      showToastMessage(err.message || 'Failed to join match. One unique ID can only fill one slot.', 'error');
    } finally {
      setIsActionPending(false);
    }
  }, [authUser, liveCounters, joinedGames, showToastMessage, isActionPending]);

  const handleConfirmWaiver = useCallback(async () => {
    if (!waiverGame) return;
    const g = waiverGame;
    setWaiverGame(null);
    if (typeof window !== 'undefined') {
      localStorage.setItem(`kheldrop_waiver_${g.id}`, 'true');
    }
    if (authUser?.id) {
      recordConsentApi(authUser.id, `VOLENTI_RISK_WAIVER_${g.sport || 'SPORT'}`, '2026.1', 'pre_match_modal').catch(() => {});
    }
    await handleJoinGame(g, true);
  }, [waiverGame, authUser, handleJoinGame]);

  const handleLeaveGame = useCallback(async (game: MockGame) => {
    if (!authUser) {
      setShowAuthModal(true);
      return;
    }
    if (isActionPending) return;

    if (isUserHostOfGame(game, authUser)) {
      showToastMessage('Hosts cannot ditch or leave their own match. Use Cancel Match to call off the event.', 'error');
      return;
    }

    setIsActionPending(true);
    const playerId = authUser?.id || authUser?.email || "player";
    const idemKey = generateIdempotencyKey(`leave_${game.id}_${playerId}`);
    const currentCount = liveCounters.get(game.id) ?? game.confirmedPlayers;

    try {
      const res = await leaveGameApi(game.id, playerId, idemKey);
      setJoinedGames(prev => {
        const next = new Set(prev);
        next.delete(game.id);
        return next;
      });
      const updatedConfirmed = typeof res?.confirmedPlayers === 'number'
        ? res.confirmedPlayers
        : Math.max(0, currentCount - 1);
      setLiveCounters(prev => {
        const next = new Map(prev);
        next.set(game.id, updatedConfirmed);
        return next;
      });
      showToastMessage(' You left the game. Slot broadcasted as open to nearby players!', 'success');
    } catch (err: any) {
      showToastMessage(err.message || 'Failed to leave game. Please try again.', 'error');
    } finally {
      setIsActionPending(false);
    }
  }, [authUser, liveCounters, showToastMessage, isActionPending]);

 const handleCheckIn = useCallback(async (game: MockGame, coords?: { lat: number; lng: number }) => {
 if (!authUser) {
 setShowAuthModal(true);
 return;
 }
 if (isActionPending) return;
 setIsActionPending(true);
 const playerId = authUser?.id || authUser?.email || "player";
 const idemKey = generateIdempotencyKey(`checkin_${game.id}_${playerId}`);

 const lat = coords?.lat ?? game.playground.lat;
 const lng = coords?.lng ?? game.playground.lng;

 try {
 await checkInApi(game.id, lat, lng, playerId, idemKey);
 showToastMessage(` Checked in successfully at ${game.playground.name}!`, 'success');
 } catch (err: any) {
 showToastMessage(err.message || `Check-in failed at ${game.playground.name}`, 'error');
 throw err;
 } finally {
 setIsActionPending(false);
 }
 }, [authUser, showToastMessage, isActionPending]);


 return (
 <div className="app-layout">
    <SEOHead
      title="Geo-Spatial Arcade and Zero-Cost Game Network"
      description="Discover live tournaments in 788+ districts, play instant zero-cost web games, and generate interactive games with AI in your neighborhood."
      canonical="/"
      keywords="kheldrop, geo-spatial games, local tournaments, browser games, zero cost arcade, AI game studio, community gaming, play drop"
    />
 
 <aside className="sidebar">
 <div className="sidebar-logo">
 <Icons.Logo />
 <span>KhelDrop</span>
 </div>

 <nav className="sidebar-nav">
 {[
 { key: 'home', icon: Icons.Home, label: 'Home' },
 { key: 'find', icon: Icons.Search, label: 'Find Game' },
 { key: 'create', icon: Icons.Plus, label: 'Create Game' },
 { key: 'mygames', icon: Icons.Calendar, label: 'My Games' },
 { key: 'messages', icon: Icons.MessageSquare, label: 'Messages' },
 { key: 'notifications', icon: Icons.Bell, label: 'Notifications', badge: unreadNotifCount > 0 ? unreadNotifCount : undefined },
 { key: 'arcade', icon: Icons.Map, label: 'Cloud Arcade', href: '/games' },
 { key: 'creator', icon: Icons.Trophy, label: 'Creator Studio', href: '/creator' },
 { key: 'onboarding', icon: Icons.Compass, label: 'Player Setup', href: '/onboarding' },
 { key: 'settings', icon: Icons.Settings, label: 'Settings', href: '/settings' },
 { key: 'profile', icon: Icons.User, label: 'Profile' },
 ].map(item => item.href ? (
 <Link
 key={item.key}
 href={item.href}
 className="nav-item"
 style={{ textDecoration: 'none' }}
 >
 <item.icon />
 {item.label}
 </Link>
 ) : (
 <button
 key={item.key}
 className={`nav-item ${activeTab === item.key ? 'active' : ''}`}
 onClick={() => {
   if (item.key === 'create') {
     if (!authUser) {
       setShowAuthModal(true);
       showToastMessage('Sign in to create and host matches', 'error');
       return;
     }
     setShowCreateModal(true);
     return;
   }
   setActiveTab(item.key);
   if (item.key === 'home') setSelectedGame(null);
 }}
 >
 <item.icon />
 {item.label}
 {item.badge && <span className="badge">{item.badge}</span>}
 </button>
 ))}
 </nav>

 {authUser ? (
 <div className="sidebar-profile">
 <div className="sidebar-profile-avatar" style={{ overflow: 'hidden' }}>
 {authUser.user_metadata?.avatar_url ? (
 <img src={authUser.user_metadata.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
 ) : (
 authUser.email?.substring(0, 2).toUpperCase() || 'PL'
 )}
 </div>
 <div className="sidebar-profile-info" style={{ overflow: 'hidden' }}>
 <div className="sidebar-profile-name" style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
 {authUser.user_metadata?.full_name || authUser.email?.split('@')[0]}
 </div>
 <div className="sidebar-profile-handle" style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', fontSize: 'var(--text-2xs)' }}>
 {authUser.email}
 </div>
 </div>
 <button
 title="Sign Out"
 onClick={async () => {
 const supabase = createClient();
 try { await supabase.auth.signOut(); } catch {}
 if (typeof window !== 'undefined') {
 localStorage.removeItem('kheldrop_auth_user');
 localStorage.removeItem('kheldrop_token');
 }
 setAuthUser(null);
 setJoinedGames(new Set());
 showToastMessage('Signed out');
 }}
 style={{ color: 'var(--text-muted)', padding: '6px', background: 'none', border: 'none', cursor: 'pointer' }}
 >
 <Icons.X />
 </button>
 </div>
 ) : (
 <div style={{ padding: '0 16px 16px' }}>
 <Link
 href="/login"
 className="join-btn"
 style={{
 width: '100%',
 height: '36px',
 fontSize: 'var(--text-xs)',
 display: 'inline-flex',
 alignItems: 'center',
 justifyContent: 'center',
 textDecoration: 'none',
 }}
 aria-label="Sign In or Join KhelDrop"
 >
 Sign In / Join
 </Link>
 </div>
 )}
 </aside>

 
 <main className="main-content">
 {selectedGame ? (
 <GameRoom
 game={selectedGame}
 liveCount={liveCounters.get(selectedGame.id) ?? selectedGame.confirmedPlayers}
 onBack={() => setSelectedGame(null)}
 onJoin={() => handleJoinGame(selectedGame)}
 onLeave={handleLeaveGame}
 onCheckIn={handleCheckIn}
 isJoined={Boolean(authUser && (joinedGames.has(selectedGame.id) || isUserPlayerInGame(selectedGame, authUser, joinedGames)))}
 isHost={Boolean(authUser && isUserHostOfGame(selectedGame, authUser))}
 isActionPending={isActionPending}
 authUser={authUser}
 />
 ) : activeTab === 'profile' ? (
 <ProfilePage
 authUser={authUser}
 onSignOut={async () => {
 const supabase = createClient();
 try { await supabase.auth.signOut(); } catch {}
 if (typeof window !== 'undefined') {
 localStorage.removeItem('kheldrop_auth_user');
 localStorage.removeItem('kheldrop_token');
 }
 setAuthUser(null);
 setJoinedGames(new Set());
 showToastMessage('Signed out');
 }}
 onRequireAuth={() => setShowAuthModal(true)}
 />
 ) : activeTab === 'notifications' ? (
 <NotificationsPage
   authUser={authUser}
   selectedDistrict={selectedDistrict}
   onSelectGameId={(gId: string) => {
     const target = games.find(g => g.id === gId);
     if (target) {
       setSelectedGame(target);
     } else {
       fetchGameApi(gId).then((bg) => {
         if (bg) setSelectedGame(bg);
       }).catch(() => {});
     }
   }}
   onUnreadCountChange={setUnreadNotifCount}
 />
 ) : activeTab === 'find' ? (
 <FindGamesPage
 games={games}
 liveCounters={liveCounters}
 joinedGames={joinedGames}
 authUser={authUser}
 userLocation={userLocation}
 onSelectGame={setSelectedGame}
 onJoinGame={handleJoinGame}
 onRequireAuth={() => setShowAuthModal(true)}
 onRequestLocation={() => requestLiveLocation(true)}
 />
 ) : activeTab === 'mygames' ? (
 <MyGamesPage
 games={games}
 joinedGames={joinedGames}
 authUser={authUser}
 onSelectGame={setSelectedGame}
 onOpenCreate={() => {
   if (!authUser) {
     setShowAuthModal(true);
     showToastMessage('Sign in to create and host matches', 'error');
     return;
   }
   setShowCreateModal(true);
 }}
 />
 ) : activeTab === 'messages' ? (
 <MessagesPage
   games={games}
   joinedGames={joinedGames}
   authUser={authUser}
   onRequireAuth={() => setShowAuthModal(true)}
   onExploreGames={() => setActiveTab('find')}
   onSelectGame={setSelectedGame}
 />
 ) : (
 <>
 
 <div className="page-header">
 <div className="header-actions">
 <div className="header-actions-left">
 <button
 className="icon-btn mobile-menu-btn"
 aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
 aria-expanded={isMobileMenuOpen}
 onClick={() => setIsMobileMenuOpen(prev => !prev)}
 title={isMobileMenuOpen ? "Close menu" : "Open menu"}
 >
 <div className="t-icon-swap" data-state={isMobileMenuOpen ? "b" : "a"}>
 <span className="t-icon" data-icon="a"><Icons.Menu /></span>
 <span className="t-icon" data-icon="b"><Icons.X /></span>
 </div>
 </button>
 </div>
 <div className="header-actions-right">
 <button
 className="location-badge"
 aria-label={`Location: ${userLocation?.name || (selectedDistrict ? `${selectedDistrict.name}, ${selectedDistrict.state}` : 'Set Location')}`}
 onClick={() => setShowLocationModal(true)}
 title="Change location"
 >
 <Icons.MapPin />
 <span className="location-text">{userLocation?.name || (selectedDistrict ? `${selectedDistrict.name}, ${selectedDistrict.state}` : 'Set Location')}</span>
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
 </button>
 <button
 className="guide-btn"
 aria-label="How KhelDrop Works & Match Guide"
 onClick={() => {
   window.dispatchEvent(new CustomEvent('kheldrop_start_tour'));
 }}
 title="How to Host & Join Matches (Guide)"
 style={{
   background: 'rgba(124, 58, 237, 0.08)',
   color: '#7c3aed',
   border: '1px solid rgba(124, 58, 237, 0.25)',
   display: 'inline-flex',
   alignItems: 'center',
   gap: '6px',
   padding: '6px 12px',
   borderRadius: '8px',
   fontSize: '12px',
   fontWeight: 700,
   cursor: 'pointer',
 }}
 >
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>
 <span>Guide</span>
 </button>
 <button className="icon-btn" aria-label={`Notifications${unreadNotifCount > 0 ? ` (${unreadNotifCount} unread)` : ''}`} onClick={() => setActiveTab('notifications')}>
<Icons.Bell />
{unreadNotifCount > 0 && <span className="dot" />}
</button>
 </div>
 </div>
 <div className="header-card">
 <h1>
 Find Your Game.<br />
 <span className="accent">Fill the Slot.</span>
 </h1>
 <p>Real people. Real games. Near you.</p>
 <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
 <button
 type="button"
 onClick={() => {
   window.dispatchEvent(new CustomEvent('kheldrop_start_tour'));
 }}
 style={{
   background: 'rgba(255, 255, 255, 0.92)',
   color: '#111827',
   border: '1px solid #e5e7eb',
   padding: '6px 13px',
   borderRadius: '999px',
   fontSize: '12px',
   fontWeight: 700,
   cursor: 'pointer',
   display: 'inline-flex',
   alignItems: 'center',
   gap: '6px',
   boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
 }}
 >
 <span style={{ color: '#7c3aed' }}><svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' style={{display: 'inline', verticalAlign: 'middle', marginRight: 4}}><path d='M6 9H4.5a2.5 2.5 0 010-5H6'/><path d='M18 9h1.5a2.5 2.5 0 000-5H18'/><path d='M4 22h16'/><path d='M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22'/><path d='M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22'/><path d='M18 2H6v7a6 6 0 0012 0V2z'/></svg></span> How to Host an Event →
 </button>
 <button
 type="button"
 onClick={() => {
   window.dispatchEvent(new CustomEvent('kheldrop_start_tour'));
 }}
 style={{
   background: 'rgba(255, 255, 255, 0.92)',
   color: '#111827',
   border: '1px solid #e5e7eb',
   padding: '6px 13px',
   borderRadius: '999px',
   fontSize: '12px',
   fontWeight: 700,
   cursor: 'pointer',
   display: 'inline-flex',
   alignItems: 'center',
   gap: '6px',
   boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
 }}
 >
 <span style={{ color: '#10b981' }}><svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' style={{display: 'inline', verticalAlign: 'middle', marginRight: 4}}><polygon points='13 2 3 14 12 14 11 22 21 10 12 10 13 2'/></svg></span> How to Join a Game →
 </button>
 </div>
 </div>
 </div>

 <div className="search-bar">
 <Icons.Search />
 <input
 placeholder="Search by sport, turf, area, or match title..."
 value={searchQuery}
 onChange={e => setSearchQuery(e.target.value)}
 />
 {searchQuery && (
 <button
 type="button"
 onClick={() => setSearchQuery('')}
 style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0 8px' }}
 >
                <Icons.X />
 </button>
 )}
 <button
   type="button"
   className={`filter-btn ${showSearchFilters || maxFilterDistanceKm < 50 || filterNeedOneOnly ? 'active' : ''}`}
   onClick={() => setShowSearchFilters(prev => !prev)}
   title="Toggle Search & Range Filters"
   style={{
     background: (showSearchFilters || maxFilterDistanceKm < 50 || filterNeedOneOnly) ? '#3B82F6' : undefined,
     color: (showSearchFilters || maxFilterDistanceKm < 50 || filterNeedOneOnly) ? '#ffffff' : undefined,
   }}
 >
 <Icons.Filter />
 </button>
 </div>

 {showSearchFilters && (
   <div
     className="animate-fade-in"
     style={{
       background: 'var(--surface, #141A18)',
       border: '1px solid var(--border, rgba(255, 255, 255, 0.1))',
       borderRadius: '14px',
       padding: '14px 16px',
       margin: '10px 0 16px',
       display: 'flex',
       flexDirection: 'column',
       gap: '12px',
       boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
     }}
   >
     <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
       <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text, #ffffff)' }}>
         Search & Range Filters
       </span>
       {(maxFilterDistanceKm < 50 || filterNeedOneOnly) && (
         <button
           type="button"
           onClick={() => {
             setMaxFilterDistanceKm(50);
             setFilterNeedOneOnly(false);
           }}
           style={{
             background: 'none',
             border: 'none',
             color: '#60A5FA',
             fontSize: '11px',
             fontWeight: 600,
             cursor: 'pointer',
             textDecoration: 'underline',
           }}
         >
           Reset Filters
         </button>
       )}
     </div>

     <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
       <span style={{ fontSize: '12px', color: 'var(--text-secondary, rgba(255,255,255,0.6))', fontWeight: 600 }}>
         Radius:
       </span>
       {[5, 10, 25, 50].map((km) => (
         <button
           key={km}
           type="button"
           onClick={() => setMaxFilterDistanceKm(km)}
           style={{
             padding: '4px 10px',
             borderRadius: '8px',
             fontSize: '12px',
             fontWeight: 600,
             border: '1px solid',
             borderColor: maxFilterDistanceKm === km ? '#3B82F6' : 'var(--border, rgba(255, 255, 255, 0.1))',
             background: maxFilterDistanceKm === km ? '#3B82F6' : 'transparent',
             color: maxFilterDistanceKm === km ? '#ffffff' : 'var(--text, rgba(255, 255, 255, 0.8))',
             cursor: 'pointer',
             transition: 'all 0.15s ease',
           }}
         >
           {km === 50 ? 'Any distance' : `${km} km`}
         </button>
       ))}
     </div>

     <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
       <button
         type="button"
         onClick={() => setFilterNeedOneOnly(prev => !prev)}
         style={{
           padding: '6px 12px',
           borderRadius: '8px',
           fontSize: '12px',
           fontWeight: 600,
           border: '1px solid',
           borderColor: filterNeedOneOnly ? '#EF4444' : 'var(--border, rgba(255, 255, 255, 0.1))',
           background: filterNeedOneOnly ? 'rgba(239, 68, 68, 0.15)' : 'transparent',
           color: filterNeedOneOnly ? '#EF4444' : 'var(--text, rgba(255, 255, 255, 0.8))',
           cursor: 'pointer',
           display: 'inline-flex',
           alignItems: 'center',
           gap: '6px',
         }}
       >
         <Icons.Flame size={14} style={{ color: "#EF4444" }} />
         <span>Only Games Needing 1 Player</span>
       </button>

       <button
         type="button"
         onClick={() => setShowLocationModal(true)}
         style={{
           padding: '6px 12px',
           borderRadius: '8px',
           fontSize: '12px',
           fontWeight: 600,
           border: '1px solid var(--border, rgba(255, 255, 255, 0.1))',
           background: 'transparent',
           color: 'var(--text, rgba(255, 255, 255, 0.8))',
           cursor: 'pointer',
           display: 'inline-flex',
           alignItems: 'center',
           gap: '6px',
         }}
       >
         <Icons.MapPin />
         <span>Change Location</span>
       </button>
     </div>
   </div>
 )}

 {searchQuery.trim() && (
   <div style={{
     display: 'flex',
     alignItems: 'center',
     justifyContent: 'space-between',
     padding: '8px 12px',
     background: 'rgba(59, 130, 246, 0.08)',
     border: '1px solid rgba(59, 130, 246, 0.2)',
     borderRadius: '10px',
     margin: '10px 0 14px',
   }}>
     <div style={{ fontSize: '12px', color: '#60A5FA', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
       <Icons.Zap size={14} style={{ color: "#60A5FA" }} />
       <span>BM25 Search: {sortedGames.length} result{sortedGames.length !== 1 ? 's' : ''} for &quot;{searchQuery}&quot;</span>
     </div>
     <button
       type="button"
       onClick={() => setSearchQuery('')}
       style={{
         background: 'none',
         border: 'none',
         color: 'var(--text-muted)',
         fontSize: '11px',
         fontWeight: 600,
         cursor: 'pointer',
         textDecoration: 'underline',
       }}
     >
       Clear
     </button>
   </div>
 )}

 
 <div className="sport-filters">
 {dynamicSportFilters.map(sf => (
 <button
 key={sf.key}
 className={`sport-chip ${activeSport === sf.key ? 'active' : ''}`}
 onClick={() => setActiveSport(sf.key)}
 >
 {sf.label} ({sf.count})
 </button>
 ))}
 </div>

 
 <div className="map-section">
 <div className="map-container">
 <GameMap
 games={filteredGames}
 liveCounters={liveCounters}
 onGameClick={(g) => setSelectedGame(g)}
 userCoords={userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : (selectedDistrict ? { lat: selectedDistrict.lat, lng: selectedDistrict.lng } : { lat: 28.6139, lng: 77.2090 })}
 userLocationName={userLocation?.name || (selectedDistrict ? `${selectedDistrict.name}, ${selectedDistrict.state}` : 'Set Location')}
 />
 </div>
 </div>

 
 <div className="games-section">
 <div className="section-header">
 <h2 className="section-title">Nearby Games</h2>
 <button className="section-link" onClick={() => setActiveTab('find')}>
 See all <Icons.ArrowRight />
 </button>
 </div>

 {sortedGames.length === 0 ? (
   searchQuery ? (
     <div
       className="animate-fade-in"
       style={{
         padding: '48px 24px',
         textAlign: 'center',
         background: 'var(--surface, #ffffff)',
         border: '1px solid var(--border, #e8e8ed)',
         borderRadius: '20px',
         margin: '12px 0 24px',
       }}
     >
       <div style={{ width: '40px', height: '40px', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}><Icons.Search /></div>
       <h3 style={{ fontSize: '17px', fontWeight: 800, margin: '0 0 6px', color: 'var(--text, #0a0a0b)' }}>
         No Games Found for &quot;{searchQuery}&quot;
       </h3>
       <p style={{ fontSize: '13px', color: 'var(--text-secondary, #6b6b76)', margin: '0 auto 18px', maxWidth: '400px', lineHeight: 1.5 }}>
         We couldn&apos;t find any open sports matches matching your keyword or active sport filter. Try expanding your search radius or clearing active filters.
       </p>
       <button
         onClick={() => {
           setSearchQuery('');
           setActiveSport('ALL');
         }}
         style={{
           background: '#111111',
           color: '#ffffff',
           border: 'none',
           borderRadius: '999px',
           padding: '10px 22px',
           fontSize: '13px',
           fontWeight: 700,
           cursor: 'pointer',
         }}
       >
         Reset Search & Filters
       </button>
     </div>
   ) : (
     <div
       className="animate-fade-in"
       style={{
         padding: '48px 24px',
         textAlign: 'center',
         background: 'var(--surface, #ffffff)',
         border: '1px solid var(--border, #e8e8ed)',
         borderRadius: '20px',
         margin: '12px 0 24px',
       }}
     >
       <div style={{ width: '40px', height: '40px', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}><Icons.Calendar /></div>
       <h3 style={{ fontSize: '17px', fontWeight: 800, margin: '0 0 6px', color: 'var(--text, #0a0a0b)' }}>
         No Matches Scheduled Yet (Empty State)
       </h3>
       <p style={{ fontSize: '13px', color: 'var(--text-secondary, #6b6b76)', margin: '0 auto 18px', maxWidth: '400px', lineHeight: 1.5 }}>
         There are currently no games open in this category. Create the first turf lobby and invite local players to join you!
       </p>
       <button
         onClick={() => {
           if (!authUser) {
             setShowAuthModal(true);
             showToastMessage('Sign in to create and host matches', 'error');
             return;
           }
           setShowCreateModal(true);
         }}
         style={{
           background: '#111111',
           color: '#ffffff',
           border: 'none',
           borderRadius: '999px',
           padding: '10px 22px',
           fontSize: '13px',
           fontWeight: 700,
           cursor: 'pointer',
         }}
       >
         Create First Game
       </button>
     </div>
   )
 ) : (
   <div className="games-grid stagger">
   {sortedGames.map((game) => {
   const liveCount = liveCounters.get(game.id) ?? game.confirmedPlayers;
   const needed = game.capacity - liveCount;
   const isNeedOne = needed === 1;
   const isFull = needed <= 0;
   const fillPercent = (liveCount / game.capacity) * 100;

   return (
   <div
   key={game.id}
   className={`game-card animate-fade-in ${isNeedOne ? 'need-one' : ''}`}
   onClick={() => setSelectedGame(game)}
   style={{ cursor: 'pointer' }}
   >
   {isNeedOne && (
   <div className="need-one-banner">
   <span className="fire"></span>
   ONE MORE PLAYER NEEDED
   </div>
   )}

   <SportCardBanner sport={game.sport} isLive={game.isLive} isNeedOne={isNeedOne} timeUntil={getTimeUntil(game.startsAt)} />

   <div className="game-card-body">
   <div className="game-card-title">{game.title}</div>
   <div className="game-card-meta">
   <Icons.MapPin />
   <span>{formatDistance(game.distance)} away</span>
   <a
   href={getGoogleMapsUrl(game.playground)}
   target="_blank"
   rel="noopener noreferrer"
   onClick={(e) => e.stopPropagation()}
   style={{
   color: 'var(--primary)',
   marginLeft: '6px',
   fontSize: '11px',
   fontWeight: 600,
   display: 'inline-flex',
   alignItems: 'center',
   gap: '2px',
   background: 'rgba(124, 58, 237, 0.12)',
   padding: '1px 6px',
   borderRadius: '4px',
   border: '1px solid rgba(124, 58, 237, 0.3)',
   textDecoration: 'none',
   }}
   title="Open directions in Google Maps"
   >
   Maps <Icons.ExternalLink />
   </a>
   <span className="dot" />
   <span>Starts {formatTime(game.startsAt)}</span>
   </div>

   <div className="player-count-section">
   <div className="player-count-row">
   <span className="player-count">
   {liveCount} <span className="slash">/</span> <span className="total">{game.capacity}</span>
   </span>
   {!isFull && (
   <span className={`need-badge ${isNeedOne ? 'urgent' : ''}`}>
   NEED {needed} MORE
   </span>
   )}
   {isFull && (
   <span style={{ color: 'var(--status-danger)', fontSize: 'var(--text-xs)', fontWeight: 600 }}>
   FULL
   </span>
   )}
   </div>
   <div className="player-bar">
   <div
   className={`player-bar-fill ${fillPercent >= 90 ? 'almost-full' : ''} ${isFull ? 'full' : ''}`}
   style={{ width: `${Math.min(fillPercent, 100)}%` }}
   />
   </div>
   </div>

   <div className="player-avatars">
   {game.players.slice(0, 4).map(p => (
   <div key={p.id} className="player-avatar">{p.initials}</div>
   ))}
   {game.players.length > 4 && (
   <div className="player-avatar more">+{game.players.length - 4}</div>
   )}
   </div>

    {(() => {
      const isHost = isUserHostOfGame(game, authUser);
      const isJoined = isUserPlayerInGame(game, authUser, joinedGames);
      return (
        <button
          className={`join-btn ${isHost ? 'host' : isJoined ? 'full' : isFull ? 'waitlist' : ''}`}
          style={isHost ? { background: 'rgba(124, 58, 237, 0.15)', color: '#8B5CF6', borderColor: 'rgba(124, 58, 237, 0.4)' } : undefined}
          onClick={(e) => {
            e.stopPropagation();
            if (isHost || isJoined) {
              setSelectedGame(game);
              return;
            }
            if (!authUser) {
              setShowAuthModal(true);
              showToastMessage('Please sign in or create an account to join matches', 'error');
              return;
            }
            handleJoinGame(game);
          }}
        >
          {isHost ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Icons.Crown size={13} /> Hosting</span>
          ) : isJoined ? 'Joined' : isFull ? 'Join Waitlist' : needed === 1 ? 'Fill Last Slot' : 'Join Game'}
        </button>
      );
    })()}
   </div>
   </div>
   );
   })}
   </div>
 )}
 </div>
 </>
 )}
 </main>

 
 {!selectedGame && (activeTab === 'home' || activeTab === 'find') && (
 <aside className="right-panel">
 
 <div style={{ marginBottom: '24px' }}>
 <div className="section-header">
 <h3 className="section-title">My Upcoming Games</h3>
 <button className="section-link" onClick={() => setActiveTab('mygames')}>
 See all <Icons.ArrowRight />
 </button>
 </div>

 {games.slice(0, 3).map(game => (
 <div key={game.id} className="upcoming-game-item" onClick={() => setSelectedGame(game)}>
 <SportAvatarBadge sport={game.sport} />
 <div className="upcoming-info">
 <div className="upcoming-name">{game.sport.charAt(0) + game.sport.slice(1).toLowerCase()}</div>
 <div className="upcoming-detail">{game.playground.name}</div>
 <div className="upcoming-detail">{getTimeUntil(game.startsAt)}</div>
 </div>
 <div className="upcoming-count">
 {liveCounters.get(game.id) ?? game.confirmedPlayers}/{game.capacity}
 </div>
 <span style={{ width: '16px', height: '16px', color: 'var(--text-muted)', flexShrink: 0 }}>
 <Icons.ChevronRight />
 </span>
 </div>
 ))}
 </div>

 
 {authUser ? (
  <div style={{ marginBottom: '24px' }}>
  <div className="section-header">
  <h3 className="section-title">Your Reliability</h3>
  </div>
  <div className="reliability-card">
  <div className="reliability-header">
  <span className="reliability-label">
  <Icons.Trophy />
  </span>
  <span className="reliability-score">
  {authUser.checkInRate !== undefined ? Math.round(authUser.checkInRate * 100) : 100}%
  </span>
  </div>
  <div className="reliability-bar">
  <div
  className="reliability-bar-fill"
  style={{
  width: `${authUser.checkInRate !== undefined ? Math.round(authUser.checkInRate * 100) : 100}%`,
  background: 'var(--brand-gradient, var(--primary))',
  }}
  />
  </div>
  <div className="reliability-stats">
  <div className="reliability-stat">
  <div className="value">{authUser.successfulGames ?? 0}</div>
  <div className="label">Games</div>
  </div>
  <div className="reliability-stat">
  <div className="value">{authUser.noShows ?? 0}</div>
  <div className="label">No-shows</div>
  </div>
  <div className="reliability-stat">
  <div className="value">{authUser.checkInRate !== undefined ? Math.round(authUser.checkInRate * 100) : 100}%</div>
  <div className="label">Check-in rate</div>
  </div>
  </div>
  </div>
  </div>
 ) : (
  <div style={{ marginBottom: '24px' }}>
  <div className="reliability-card" style={{ textAlign: 'center', padding: '20px 16px' }}>
   <div style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', color: 'var(--accent)' }}><Icons.Trophy /></div>
  <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)', marginBottom: '4px' }}>
  Track Your Reliability
  </div>
  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: 1.4 }}>
  Sign in to build your player score and join matches near you.
  </p>
  <button
  className="join-btn"
  onClick={() => setShowAuthModal(true)}
  style={{ width: '100%', padding: '8px 12px', fontSize: '13px' }}
  >
  Sign In / Join
  </button>
  </div>
  </div>
 )}
 </aside>
 )}

 
 {/* Mobile Navigation Drawer with Desktop Parity */}
 <div
 className={`mobile-drawer-overlay ${isMobileMenuOpen ? 'open' : ''}`}
 onClick={() => setIsMobileMenuOpen(false)}
 aria-hidden={!isMobileMenuOpen}
 />
 <div
 className={`mobile-drawer ${isMobileMenuOpen ? 'open' : ''}`}
 role="dialog"
 aria-modal="true"
 aria-label="Navigation Menu"
 >
 <div className="mobile-drawer-header">
 <div className="mobile-drawer-logo">
 <Icons.Logo />
 <span>KhelDrop</span>
 </div>
 <button
 className="mobile-drawer-close"
 onClick={() => setIsMobileMenuOpen(false)}
 aria-label="Close navigation menu"
 >
 <Icons.X />
 </button>
 </div>

 <div className="mobile-drawer-content">
 <div style={{ padding: '0 4px' }}>
 <button
 className="location-badge"
 style={{ width: '100%', justifyContent: 'space-between', padding: '10px 14px' }}
 onClick={() => {
 setIsMobileMenuOpen(false);
 setShowLocationModal(true);
 }}
 title="Change location"
 >
 <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
 <Icons.MapPin />
 <span className="location-text" style={{ maxWidth: '180px' }}>{userLocation?.name || (selectedDistrict ? `${selectedDistrict.name}, ${selectedDistrict.state}` : 'Set Location')}</span>
 </span>
 <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Change</span>
 </button>
 </div>

 <div className="mobile-drawer-section">
 <div className="mobile-drawer-section-title">Navigation</div>
 <nav className="mobile-drawer-nav">
 {[
 { key: 'home', icon: Icons.Home, label: 'Home' },
 { key: 'find', icon: Icons.Search, label: 'Find Game' },
 { key: 'create', icon: Icons.Plus, label: 'Create Game' },
 { key: 'mygames', icon: Icons.Calendar, label: 'My Games' },
 { key: 'messages', icon: Icons.MessageSquare, label: 'Messages' },
 { key: 'notifications', icon: Icons.Bell, label: 'Notifications', badge: unreadNotifCount > 0 ? unreadNotifCount : undefined },
 { key: 'profile', icon: Icons.User, label: 'Profile' },
 ].map(item => (
 <button
 key={item.key}
 className={`mobile-drawer-item ${activeTab === item.key ? 'active' : ''}`}
 onClick={() => {
 setIsMobileMenuOpen(false);
 if (item.key === 'create') {
   if (!authUser) {
     setShowAuthModal(true);
     showToastMessage('Sign in to create and host matches', 'error');
     return;
   }
   setShowCreateModal(true);
   return;
 }
 setActiveTab(item.key);
 if (item.key === 'home') setSelectedGame(null);
 }}
 >
 <item.icon />
 <span>{item.label}</span>
 {item.badge && <span className="badge">{item.badge}</span>}
 </button>
 ))}
 </nav>
 </div>

 <div className="mobile-drawer-section">
 <div className="mobile-drawer-section-title">Portals & Studio</div>
 <nav className="mobile-drawer-nav">
 <Link
 href="/games"
 className="mobile-drawer-item"
 onClick={() => setIsMobileMenuOpen(false)}
 >
 <Icons.Map />
 <span>Cloud Arcade</span>
 <span className="badge-pill">Play</span>
 </Link>
 <Link
 href="/creator"
 className="mobile-drawer-item"
 onClick={() => setIsMobileMenuOpen(false)}
 >
 <Icons.Trophy />
 <span>Creator Studio</span>
 <span className="badge-pill">Studio</span>
 </Link>
 <Link
 href="/settings"
 className="mobile-drawer-item"
 onClick={() => setIsMobileMenuOpen(false)}
 >
 <Icons.Settings />
 <span>Account Settings</span>
 <span className="badge-pill">Prefs</span>
 </Link>
 <Link
 href="/onboarding"
 className="mobile-drawer-item"
 onClick={() => setIsMobileMenuOpen(false)}
 >
 <Icons.Compass />
 <span>Player Setup</span>
 <span className="badge-pill">Start</span>
 </Link>
 <button
 className="mobile-drawer-item"
 onClick={() => {
   setIsMobileMenuOpen(false);
   window.dispatchEvent(new CustomEvent('kheldrop_start_tour'));
 }}
 style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
 >
 <Icons.Zap />
 <span>How to Host & Join</span>
 <span className="badge-pill" style={{ background: '#7c3aed', color: '#fff' }}>Guide</span>
 </button>
 </nav>
 </div>

 <div className="mobile-drawer-section">
 <div className="mobile-drawer-section-title">Trust, Legal & Support</div>
 <div style={{ padding: '0 4px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
   <Link
     href="/help"
     onClick={() => setIsMobileMenuOpen(false)}
     style={{
       display: 'flex',
       alignItems: 'center',
       justifyContent: 'space-between',
       padding: '10px 12px',
       borderRadius: '12px',
       background: 'rgba(109, 40, 217, 0.06)',
       color: '#6d28d9',
       fontWeight: 700,
       fontSize: '13px',
       textDecoration: 'none',
       border: '1px solid rgba(109, 40, 217, 0.15)',
     }}
   >
     <span>Help Center & FAQ</span>
     <span style={{ fontSize: '10px', background: '#6d28d9', color: '#fff', padding: '2px 7px', borderRadius: '999px' }}>Support</span>
   </Link>
   <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginTop: '2px' }}>
     <Link href="/terms" onClick={() => setIsMobileMenuOpen(false)} style={{ fontSize: '12px', color: 'var(--text-secondary)', textDecoration: 'none', padding: '7px 9px', borderRadius: '8px', background: 'var(--elevated)' }}>Terms</Link>
     <Link href="/privacy" onClick={() => setIsMobileMenuOpen(false)} style={{ fontSize: '12px', color: 'var(--text-secondary)', textDecoration: 'none', padding: '7px 9px', borderRadius: '8px', background: 'var(--elevated)' }}>Privacy</Link>
     <Link href="/community-guidelines" onClick={() => setIsMobileMenuOpen(false)} style={{ fontSize: '12px', color: 'var(--text-secondary)', textDecoration: 'none', padding: '7px 9px', borderRadius: '8px', background: 'var(--elevated)' }}>Community</Link>
     <Link href="/safety" onClick={() => setIsMobileMenuOpen(false)} style={{ fontSize: '12px', color: 'var(--text-secondary)', textDecoration: 'none', padding: '7px 9px', borderRadius: '8px', background: 'var(--elevated)' }}>Safety</Link>
     <Link href="/location-privacy" onClick={() => setIsMobileMenuOpen(false)} style={{ fontSize: '12px', color: 'var(--text-secondary)', textDecoration: 'none', padding: '7px 9px', borderRadius: '8px', background: 'var(--elevated)' }}>Location</Link>
     <Link href="/grievance" onClick={() => setIsMobileMenuOpen(false)} style={{ fontSize: '12px', color: 'var(--text-secondary)', textDecoration: 'none', padding: '7px 9px', borderRadius: '8px', background: 'var(--elevated)' }}>Grievance</Link>
     <Link href="/copyright" onClick={() => setIsMobileMenuOpen(false)} style={{ fontSize: '12px', color: 'var(--text-secondary)', textDecoration: 'none', padding: '7px 9px', borderRadius: '8px', background: 'var(--elevated)' }}>Copyright</Link>
     <Link href="/settings" onClick={() => setIsMobileMenuOpen(false)} style={{ fontSize: '12px', color: 'var(--text-secondary)', textDecoration: 'none', padding: '7px 9px', borderRadius: '8px', background: 'var(--elevated)' }}>Privacy Center</Link>
   </div>
 </div>
 </div>

 <div className="mobile-drawer-section">
 <div className="mobile-drawer-section-title">Player Reliability</div>
 {authUser ? (
 <div className="reliability-card" style={{ padding: '14px', borderRadius: 'var(--radius-md)' }}>
 <div className="reliability-header" style={{ marginBottom: '8px' }}>
 <span className="reliability-label" style={{ fontSize: '12px' }}>
 <Icons.Trophy />
 </span>
 <span className="reliability-score" style={{ fontSize: '15px' }}>
 {authUser.checkInRate !== undefined ? Math.round(authUser.checkInRate * 100) : 100}%
 </span>
 </div>
 <div className="reliability-bar" style={{ height: '5px' }}>
 <div
 className="reliability-bar-fill"
 style={{
 width: `${authUser.checkInRate !== undefined ? Math.round(authUser.checkInRate * 100) : 100}%`,
 background: 'var(--brand-gradient, var(--primary))',
 }}
 />
 </div>
 <div className="reliability-stats" style={{ marginTop: '10px' }}>
 <div className="reliability-stat">
 <div className="value" style={{ fontSize: '13px' }}>{authUser.successfulGames ?? 0}</div>
 <div className="label" style={{ fontSize: '10px' }}>Games</div>
 </div>
 <div className="reliability-stat">
 <div className="value" style={{ fontSize: '13px' }}>{authUser.noShows ?? 0}</div>
 <div className="label" style={{ fontSize: '10px' }}>No-shows</div>
 </div>
 <div className="reliability-stat">
 <div className="value" style={{ fontSize: '13px' }}>
 {authUser.checkInRate !== undefined ? Math.round(authUser.checkInRate * 100) : 100}%
 </div>
 <div className="label" style={{ fontSize: '10px' }}>Check-in</div>
 </div>
 </div>
 </div>
 ) : (
 <div className="reliability-card" style={{ padding: '14px', textAlign: 'center', borderRadius: 'var(--radius-md)' }}>
 <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
 Build Match Reputation
 </div>
 <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '10px', lineHeight: 1.4 }}>
 Sign in to track reliability score and unlock instant match joins.
 </p>
 <button
 className="join-btn"
 onClick={() => {
 setIsMobileMenuOpen(false);
 setShowAuthModal(true);
 }}
 style={{ width: '100%', padding: '6px 10px', fontSize: '12px' }}
 >
 Sign In / Join
 </button>
 </div>
 )}
 </div>

 {games.length > 0 && (
 <div className="mobile-drawer-section">
 <div className="mobile-drawer-section-title">Upcoming Games</div>
 <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
 {games.slice(0, 2).map(game => (
 <div
 key={game.id}
 className="upcoming-game-item"
 style={{ padding: '8px 10px' }}
 onClick={() => {
 setIsMobileMenuOpen(false);
 setSelectedGame(game);
 }}
 >
 <SportAvatarBadge sport={game.sport} />
 <div className="upcoming-info">
 <div className="upcoming-name">{game.sport.charAt(0) + game.sport.slice(1).toLowerCase()}</div>
 <div className="upcoming-detail">{game.playground.name}</div>
 </div>
 <div className="upcoming-count" style={{ fontSize: '12px' }}>
 {liveCounters.get(game.id) ?? game.confirmedPlayers}/{game.capacity}
 </div>
 </div>
 ))}
 </div>
 </div>
 )}
 </div>

 <div className="mobile-drawer-footer">
 {authUser ? (
 <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
 <div
 style={{
 width: '38px',
 height: '38px',
 borderRadius: '50%',
 background: 'var(--brand-gradient, var(--primary))',
 color: '#fff',
 display: 'flex',
 alignItems: 'center',
 justifyContent: 'center',
 fontSize: '13px',
 fontWeight: 700,
 overflow: 'hidden',
 flexShrink: 0,
 }}
 >
 {authUser.user_metadata?.avatar_url ? (
 <img
 src={authUser.user_metadata.avatar_url}
 alt=""
 style={{ width: '100%', height: '100%', objectFit: 'cover' }}
 />
 ) : (
 authUser.email?.substring(0, 2).toUpperCase() || 'PL'
 )}
 </div>
 <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
 <div
 style={{
 fontSize: '13px',
 fontWeight: 600,
 color: 'var(--text)',
 whiteSpace: 'nowrap',
 textOverflow: 'ellipsis',
 overflow: 'hidden',
 }}
 >
 {authUser.user_metadata?.full_name || authUser.email?.split('@')[0]}
 </div>
 <div
 style={{
 fontSize: '11px',
 color: 'var(--text-muted)',
 whiteSpace: 'nowrap',
 textOverflow: 'ellipsis',
 overflow: 'hidden',
 }}
 >
 {authUser.email}
 </div>
 </div>
 <button
 title="Sign Out"
 onClick={async () => {
 setIsMobileMenuOpen(false);
 const supabase = createClient();
 try { await supabase.auth.signOut(); } catch {}
 if (typeof window !== 'undefined') {
 localStorage.removeItem('kheldrop_auth_user');
 localStorage.removeItem('kheldrop_token');
 }
 setAuthUser(null);
 setJoinedGames(new Set());
 showToastMessage('Signed out');
 }}
 style={{
 color: 'var(--text-muted)',
 padding: '6px',
 background: 'none',
 border: 'none',
 cursor: 'pointer',
 display: 'flex',
 alignItems: 'center',
 }}
 aria-label="Sign Out"
 >
 <Icons.X />
 </button>
 </div>
 ) : (
 <button
 className="join-btn"
 onClick={() => {
 setIsMobileMenuOpen(false);
 setShowAuthModal(true);
 }}
 style={{
 width: '100%',
 height: '38px',
 fontSize: 'var(--text-xs)',
 display: 'inline-flex',
 alignItems: 'center',
 justifyContent: 'center',
 }}
 aria-label="Sign In or Join KhelDrop"
 >
 Sign In / Join
 </button>
 )}
 </div>
 </div>

 {showCookieConsent && (
   <div className="cookie-consent-overlay animate-fade-in" role="dialog" aria-label="Cookie Preferences">
     <div className="cookie-consent-card">
       <div className="cookie-consent-header">
          <div className="cookie-consent-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: "28px", height: "28px" }}><circle cx="12" cy="12" r="10"/><circle cx="8" cy="8" r="1.5" fill="currentColor"/><circle cx="15" cy="10" r="1.5" fill="currentColor"/><circle cx="10" cy="15" r="1.5" fill="currentColor"/><circle cx="16" cy="16" r="1" fill="currentColor"/></svg></div>
         <div>
           <h4 className="cookie-consent-title">We value your privacy & consent</h4>
           <p className="cookie-consent-desc">
             KhelDrop uses cookies strictly to keep you logged in and remember your active sport radar filters. No ad tracking or unsolicited data sharing. Read our{' '}
             <Link href="/cookies" style={{ color: '#111111', fontWeight: 600, textDecoration: 'underline' }}>
               Cookie Policy
             </Link>{' '}
             and{' '}
             <Link href="/privacy" style={{ color: '#111111', fontWeight: 600, textDecoration: 'underline' }}>
               Privacy Policy
             </Link>.
           </p>
         </div>
       </div>
       <div className="cookie-consent-actions">
         <Link
           href="/cookie-preferences"
           className="cookie-btn cookie-btn-essential"
           style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
         >
           Preferences
         </Link>
         <button
           className="cookie-btn cookie-btn-essential"
           onClick={() => {
             if (typeof window !== 'undefined') {
               localStorage.setItem('kheldrop_cookie_consent', 'essential');
               localStorage.setItem('kheldrop_cookie_settings', JSON.stringify({
                 essential: true,
                 performance: false,
                 functional: false,
                 matchmaking: false,
               }));
             }
             setShowCookieConsent(false);
             showToastMessage('Essential cookies enabled');
           }}
         >
           Essential Only
         </button>
         <button
           className="cookie-btn cookie-btn-accept"
           onClick={() => {
             if (typeof window !== 'undefined') {
               localStorage.setItem('kheldrop_cookie_consent', 'all');
               localStorage.setItem('kheldrop_cookie_settings', JSON.stringify({
                 essential: true,
                 performance: true,
                 functional: true,
                 matchmaking: true,
               }));
             }
             setShowCookieConsent(false);
             showToastMessage('All preferences accepted');
           }}
         >
           Accept All
         </button>
       </div>
     </div>
   </div>
 )}

 <BottomNav
 active={activeTab}
 onNavigate={(page) => {
 setActiveTab(page);
 if (page === 'home') setSelectedGame(null);
 }}
 onCreate={() => {
   if (!authUser) {
     setShowAuthModal(true);
     showToastMessage('Sign in to create and host matches', 'error');
     return;
   }
   setShowCreateModal(true);
 }}
 />

 <div className={`toast ${showToast ? 'show' : ''} ${toastType}`}>
 {toastMessage}
 </div>

 <GameGuideModal
 isOpen={showGuideModal}
 onClose={() => setShowGuideModal(false)}
 initialMode={guideInitialMode}
 onOpenCreate={() => {
   setShowGuideModal(false);
   if (!authUser) {
     setShowAuthModal(true);
     showToastMessage('Sign in to create and host matches', 'error');
   } else {
     setShowCreateModal(true);
   }
 }}
 onExploreGames={() => {
   setShowGuideModal(false);
   setActiveTab('find');
 }}
 />

 
 {showCreateModal && (
 <CreateGameModal
 onClose={() => setShowCreateModal(false)}
 onCreated={(newGame, msg) => {
 setGames(prev => [newGame, ...prev.filter(g => g.id !== newGame.id)]);
 setLiveCounters(prev => {
 const next = new Map(prev);
 next.set(newGame.id, newGame.confirmedPlayers);
 return next;
 });
 setJoinedGames(prev => new Set([...prev, newGame.id]));
 setSelectedGame(newGame);
 showToastMessage(msg, 'success');
 }}
 authUser={authUser}
 onRequireAuth={() => setShowAuthModal(true)}
 userLocation={userLocation}
 selectedDistrict={selectedDistrict}
 />
 )}

 
      {waiverGame && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999999,
            background: 'rgba(0, 0, 0, 0.82)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            animation: 'fadeIn 0.2s ease-out',
          }}
          onClick={() => setWaiverGame(null)}
        >
          <div
            style={{
              maxWidth: '500px',
              width: '100%',
              background: '#0f172a',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: '20px',
              padding: '24px 26px',
              color: '#f8fafc',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.75)',
              fontFamily: 'Inter, system-ui, sans-serif',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
              <div
                style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '10px',
                  background: 'rgba(239, 68, 68, 0.15)',
                  color: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#ffffff' }}>
                  Safety & Physical Risk Waiver
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#94a3b8' }}>
                  Voluntary Participation • {waiverGame.sport} at {waiverGame.venue}
                </p>
              </div>
            </div>

            <div
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '12px',
                padding: '14px 16px',
                fontSize: '12.5px',
                lineHeight: 1.5,
                color: '#cbd5e1',
                marginBottom: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <div>
                <strong style={{ color: '#ffffff' }}>1. Physical Fitness:</strong> You affirm that you are medically fit and have no cardiovascular or health conditions making active sport unsafe.
              </div>
              <div>
                <strong style={{ color: '#ffffff' }}>2. Voluntary Assumption of Risk (Volenti Non Fit Injuria):</strong> You understand that athletic sports involve inherent risks of injury, collisions, falls, or sudden accidents, and you voluntarily choose to participate at your own risk.
              </div>
              <div>
                <strong style={{ color: '#ffffff' }}>3. Intermediary Status (§79 IT Act):</strong> KhelDrop is a recreational matchmaking platform and does not supervise venues, referees, or independent player conduct.
              </div>
              <div>
                <strong style={{ color: '#ffffff' }}>4. No Gambling / Zero Violence:</strong> Betting, wagering, and physical aggression are strictly prohibited.
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setWaiverGame(null)}
                style={{
                  padding: '9px 16px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#94a3b8',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmWaiver}
                style={{
                  padding: '9px 20px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                  border: 'none',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(124, 58, 237, 0.35)',
                }}
              >
                I Affirm & Join Match
              </button>
            </div>
          </div>
        </div>
      )}

 <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={(user) => {
          setAuthUser(user);
          // Clean demo data on login
          setGames([]);
          setJoinedGames(new Set());
          showToastMessage(`Welcome to KhelDrop, ${user.user_metadata?.full_name || user.email}!`);

          // Ask to add location on login if not set
          const hasLoc = localStorage.getItem(`kheldrop_location_set_${user.id}`) || user.districtName;
          if (!hasLoc) {
            setShowLocationModal(true);
          }
        }}
      />

 <LocationModal
   isOpen={showLocationModal}
   onClose={() => setShowLocationModal(false)}
   authUser={authUser}
   userLocation={userLocation}
   isLocating={isLocating}
   locationError={locationError}
   onRequestLiveGps={() => {
     setShowLocationModal(false);
     requestLiveLocation(true);
   }}
   onSelectHub={handleSelectHub}
   onOpenDistrictSelector={() => {
     setShowLocationModal(false);
     setShowDistrictSelector(true);
   }}
   onRequireAuth={() => setShowAuthModal(true)}
 />

 <DistrictSelector
   isOpen={showDistrictSelector}
   onClose={() => setShowDistrictSelector(false)}
   userLat={pendingGeoForDistrict?.lat || userLocation?.lat}
   userLng={pendingGeoForDistrict?.lng || userLocation?.lng}
   currentDistrict={selectedDistrict ? `${selectedDistrict.name}, ${selectedDistrict.state}` : userLocation?.name}
   userId={authUser?.id || (authUser as any)?._id}
   onDistrictSelected={handleDistrictSelected}
 />
 </div>
 );
}


function GameRoom({ game, liveCount, onBack, onJoin, onLeave, onCheckIn, isJoined, isHost, isActionPending, authUser }: {
 game: MockGame;
 liveCount: number;
 onBack: () => void;
 onJoin: () => void;
 onLeave?: (game: MockGame) => void;
 onCheckIn?: (game: MockGame, coords?: { lat: number; lng: number }) => Promise<void> | void;
 isJoined: boolean;
 isHost?: boolean;
 isActionPending?: boolean;
 authUser?: any;
}) {
 const needed = game.capacity - liveCount;
 const isFull = needed <= 0;
  const hasChatAccess = Boolean(isJoined || isHost);

  // Live 1-second interval countdown timer
  const [nowTime, setNowTime] = useState<number>(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNowTime(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const startsAtMs = useMemo(() => new Date(game.startsAt).getTime(), [game.startsAt]);
  const BUFFER_MS = 10 * 60 * 1000; // 10-minute check-in grace buffer
  const bufferEndsAtMs = startsAtMs + BUFFER_MS;
  const isStarted = nowTime >= startsAtMs;
  const isBufferActive = isStarted && nowTime <= bufferEndsAtMs;
  const isPastBuffer = nowTime > bufferEndsAtMs;

  const formatCountdown = (diffMs: number) => {
    if (diffMs <= 0) return '00:00';
    const totalSecs = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    if (hours > 0) {
      return `${hours}h ${mins.toString().padStart(2, '0')}m ${secs.toString().padStart(2, '0')}s`;
    }
    return `${mins.toString().padStart(2, '0')}m ${secs.toString().padStart(2, '0')}s`;
  };


 const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
 const [distanceToGround, setDistanceToGround] = useState<number | null>(null);
 const [isSimulatedArrival, setIsSimulatedArrival] = useState(false);
 const [isCheckedIn, setIsCheckedIn] = useState(false);
 const [isGpsLoading, setIsGpsLoading] = useState(false);
 const [gpsError, setGpsError] = useState('');

 const GEOFENCE_RADIUS_METERS = 200; // Geofence requirement: 200m from turf

 useEffect(() => {
 if (!isJoined && !isHost) return;
 if (!navigator.geolocation) {
 setGpsError('Geolocation is not supported by your browser');
 return;
 }

 setIsGpsLoading(true);

 const updatePosition = (lat: number, lng: number) => {
 setUserCoords({ lat, lng });
 const dist = calculateDistanceMeters(lat, lng, game.playground.lat, game.playground.lng);
 setDistanceToGround(dist);
 setIsGpsLoading(false);
 setGpsError('');
 };

 navigator.geolocation.getCurrentPosition(
 (pos) => updatePosition(pos.coords.latitude, pos.coords.longitude),
 (err) => {
 setIsGpsLoading(false);
 setGpsError(err.message || 'GPS location unavailable');
 },
 { enableHighAccuracy: true, timeout: 10000 }
 );

	// DPDP Data Minimisation: Single-event GPS verification; no continuous background tracking
 }, [isJoined, game.playground.lat, game.playground.lng, isSimulatedArrival]);

 const isAtVenue = distanceToGround !== null && distanceToGround <= GEOFENCE_RADIUS_METERS;

 const toggleSimulateArrival = () => {
 if (!isSimulatedArrival) {
 setIsSimulatedArrival(true);
 setUserCoords({ lat: game.playground.lat, lng: game.playground.lng });
 setDistanceToGround(15);
 setGpsError('');
 } else {
 setIsSimulatedArrival(false);
 navigator.geolocation.getCurrentPosition((pos) => {
 setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
 setDistanceToGround(calculateDistanceMeters(pos.coords.latitude, pos.coords.longitude, game.playground.lat, game.playground.lng));
 });
 }
 };

 const handleDoCheckIn = async () => {
 if (!onCheckIn) return;
 const coords = userCoords || { lat: game.playground.lat, lng: game.playground.lng };
 try {
 await onCheckIn(game, coords);
 setIsCheckedIn(true);
 } catch {}
 };

 return (
 <div className="game-room animate-fade-in">
 
 <button
 onClick={onBack}
 style={{
 display: 'flex', alignItems: 'center', gap: '8px',
 color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 500,
 marginBottom: '16px', padding: '6px 0',
 }}
 >
 <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
 Back to games
 </button>

 
 <div className="game-room-header">
 <div className="game-room-sport">
 <span style={{ fontSize: '20px' }}>{game.sportEmoji}</span>
 {game.sport}
 {game.isLive && (
 <span className="live-badge" style={{ position: 'static' }}>
 <span className="pulse" />
 LIVE
 </span>
 )}
 </div>
 <h1 className="game-room-title">{game.playground.name}</h1>
 <div className="game-room-meta" style={{ flexWrap: 'wrap', gap: '8px' }}>
 <a
 href={getGoogleMapsUrl(game.playground)}
 target="_blank"
 rel="noopener noreferrer"
 className="maps-direction-pill"
 title="Open directions in Google Maps"
 >
 <Icons.MapPin />
 <span>{formatDistance(game.distance)} away</span>
 <span style={{ opacity: 0.5 }}>•</span>
 <span> Google Maps <Icons.ExternalLink /></span>
 </a>
 <span>•</span>
 <span>{formatTime(game.startsAt)} to {formatTime(game.endsAt)}</span>
 <span>•</span>
 <span>Hosted by {game.hostName}</span>
 </div>
 </div>

 
 <div className="game-room-count">
 <div className="big-count">
 {liveCount} <span className="slash">/</span> <span className="total">{game.capacity}</span>
 </div>
 <div className="count-label">Players</div>
 {!isFull && (
 <div className="need-text">
 {needed === 1 ? ' Need 1 more player!' : <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><svg width="6" height="6" viewBox="0 0 6 6" fill="currentColor"><circle cx="3" cy="3" r="3"/></svg> Need {needed} more</span>}
 </div>
 )}
 {isFull && (
 <div className="need-text" style={{ color: 'var(--status-warning)' }}>
 Game is full
 </div>
 )}
 </div>

 
 <div style={{ marginBottom: '24px' }}>
 <div className="player-bar" style={{ height: '8px' }}>
 <div
 className={`player-bar-fill ${(liveCount / game.capacity) >= 0.9 ? 'almost-full' : ''}`}
 style={{ width: `${(liveCount / game.capacity) * 100}%` }}
 />
 </div>
 </div>

 
    {/* MATCH COUNTDOWN & 10-MINUTE CHECK-IN BUFFER BANNER */}
  <div style={{
    borderRadius: '16px',
    padding: '16px 20px',
    marginBottom: '20px',
    background: isBufferActive 
      ? 'rgba(245, 158, 11, 0.1)' 
      : isPastBuffer 
      ? 'rgba(100, 116, 139, 0.08)' 
      : 'rgba(124, 58, 237, 0.08)',
    border: isBufferActive 
      ? '1.5px solid rgba(245, 158, 11, 0.4)' 
      : isPastBuffer 
      ? '1px solid var(--border)' 
      : '1.5px solid rgba(124, 58, 237, 0.3)',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {isBufferActive ? (
            <Icons.AlertTriangle size={20} style={{ color: '#F59E0B' }} />
          ) : isPastBuffer ? (
            <Icons.Clock size={20} style={{ color: '#64748B' }} />
          ) : (
            <Icons.Hourglass size={20} style={{ color: '#8B5CF6' }} />
          )}
        </span>
        <div>
          <div style={{
            fontSize: '14px',
            fontWeight: 700,
            color: isBufferActive ? 'var(--status-warning, #f59e0b)' : 'var(--text)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            {!isStarted ? (
              <>
                <span>Match Starts In:</span>
                <span style={{
                  fontFamily: 'monospace',
                  fontSize: '15px',
                  fontWeight: 800,
                  color: 'var(--primary)',
                  background: 'rgba(124, 58, 237, 0.12)',
                  padding: '2px 8px',
                  borderRadius: '6px',
                }}>
                  {formatCountdown(startsAtMs - nowTime)}
                </span>
              </>
            ) : isBufferActive ? (
              <>
                <span style={{ color: '#d97706' }}>10-Min Check-in Grace Buffer Active:</span>
                <span style={{
                  fontFamily: 'monospace',
                  fontSize: '15px',
                  fontWeight: 800,
                  color: '#b45309',
                  background: 'rgba(245, 158, 11, 0.2)',
                  padding: '2px 8px',
                  borderRadius: '6px',
                  animation: 'pulse 1.5s infinite',
                }}>
                  {formatCountdown(bufferEndsAtMs - nowTime)} left
                </span>
              </>
            ) : (
              <span>Match in Progress</span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '3px', lineHeight: 1.4 }}>
            {!isStarted ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <Icons.Shield size={14} style={{ color: '#10B981', flexShrink: 0 }} />
                <span><strong>100% Reliability Protected:</strong> Score only reduces if you fail to check in before match starts + 10-minute grace buffer.</span>
              </span>
            ) : isBufferActive ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <Icons.Siren size={14} style={{ color: '#EF4444', flexShrink: 0 }} />
                <span>Check in within 200m of the venue now! Your 100% reliability score is protected during this 10-minute buffer window.</span>
              </span>
            ) : isCheckedIn ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#10B981' }}>
                <Icons.Check size={14} style={{ flexShrink: 0 }} />
                <span>You checked in on time. Your 100% reliability score is preserved!</span>
              </span>
            ) : (
              <span style={{ color: 'var(--status-danger)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <Icons.AlertTriangle size={14} style={{ flexShrink: 0 }} />
                <span>10-minute check-in grace buffer has ended. Unchecked slots subject to ditch penalties.</span>
              </span>
            )}
          </div>
        </div>
      </div>
      <div style={{
        fontSize: '12px',
        fontWeight: 600,
        padding: '4px 10px',
        borderRadius: '20px',
        background: isCheckedIn ? 'rgba(16, 185, 129, 0.12)' : 'rgba(124, 58, 237, 0.1)',
        color: isCheckedIn ? 'var(--status-success)' : 'var(--primary)',
      }}>
        {isCheckedIn ? 'Checked In' : '10-Min Buffer Protected'}
      </div>
    </div>
  </div>

{/* RBAC: HOST VIEW */}
  {isHost ? (
    <div style={{
      background: 'rgba(124, 58, 237, 0.08)',
      border: '1.5px solid rgba(124, 58, 237, 0.3)',
      borderRadius: '16px',
      padding: '16px 20px',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '38px',
          height: '38px',
          borderRadius: '50%',
          background: 'rgba(124, 58, 237, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#8B5CF6',
        }}>
          <Icons.Crown size={20} />
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: '15px', color: '#8B5CF6' }}>You are Hosting this Match</div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            Slot 1 of {game.capacity} is confirmed for you. {needed > 0 ? `Waiting for ${needed} more player${needed > 1 ? 's' : ''} to fill.` : 'All slots are filled! Match is ready.'}
          </div>
        </div>
      </div>
      <span style={{
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: 700,
        background: 'rgba(124, 58, 237, 0.2)',
        color: '#A78BFA',
        border: '1px solid rgba(124, 58, 237, 0.4)',
        letterSpacing: '0.05em',
      }}>
        HOST
      </span>
    </div>
  ) : !isJoined ? (
    <button
      className={`join-btn ${isFull ? 'waitlist' : ''}`}
      style={{ marginBottom: '24px', padding: '14px', fontSize: '16px' }}
      onClick={onJoin}
      disabled={isActionPending}
    >
      {isActionPending ? 'Processing...' : isFull ? 'Join Waitlist' : needed === 1 ? ' Fill the Last Slot!' : 'Join Game'}
    </button>
  ) : null}
  {(isJoined || isHost) && (
 <div className="checkin-container">
 {isCheckedIn ? (
 <div className="checkin-badge checked-in">
 <span className="dot pulse" />
 <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> You are checked in at {game.playground.name}! Match slot confirmed.</span>
 </div>
 ) : isAtVenue ? (
 <div className="checkin-badge ready">
 <span className="dot pulse" />
 <span> You have arrived! ({distanceToGround}m away) — Check-in unlocked!</span>
 </div>
 ) : (
 <div className="checkin-badge locked">
 <span></span>
 <span>
 {distanceToGround !== null
 ? `${formatDistance(distanceToGround)} away from venue • Check-in unlocks within ${GEOFENCE_RADIUS_METERS}m of turf`
 : isGpsLoading
 ? 'Acquiring live GPS location...'
 : gpsError
 ? `GPS: ${gpsError}`
 : 'Awaiting device GPS...'}
 </span>
 </div>
 )}

 <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
 <button
 type="button"
 className={`checkin-btn ${isCheckedIn ? 'done' : isAtVenue ? 'active' : 'disabled'}`}
 onClick={handleDoCheckIn}
 disabled={!isAtVenue || isActionPending || isCheckedIn}
 title={
 isCheckedIn
 ? 'Checked in'
 : isAtVenue
 ? 'Confirm your arrival at the venue'
 : `Reach within ${GEOFENCE_RADIUS_METERS}m of ${game.playground.name} to check in`
 }
 >
 {isCheckedIn ? (
 'Checked In'
 ) : isAtVenue ? (
 ' Check In Now'
 ) : (
 ` Check In (${distanceToGround !== null ? `${formatDistance(distanceToGround)} away` : 'Locked'})`
 )}
 </button>

 {!isHost && (
 <button
 type="button"
 className="leave-btn"
 onClick={() => onLeave && onLeave(game)}
 disabled={isActionPending}
 title="Leave match or ditch slot. Releases slot to nearby players instantly via Redis."
 >
 Leave / Ditch
 </button>
 )}
 </div>

 
 <div style={{
 display: 'flex',
 justifyContent: 'space-between',
 alignItems: 'center',
 marginTop: '10px',
 fontSize: '11px',
 color: 'var(--text-muted)',
 flexWrap: 'wrap',
 gap: '8px',
 }}>
 <span>
 Live GPS: {userCoords ? (isSimulatedArrival ? 'Simulated at Venue' : 'Active (Privacy Shield)') : 'Detecting...'}
 </span>
 <button
 type="button"
 onClick={toggleSimulateArrival}
 className="simulate-geofence-btn"
 title="Simulate GPS coordinates at the venue for testing"
 >
 {isSimulatedArrival ? ' Reset to Device GPS' : ' Test: Simulate Arriving at Venue'}
 </button>
 </div>
 </div>
 )}


 
 <div className="player-list">
 <div className="player-list-header">
 <span>Players ({game.players.length})</span>
 <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Status</span>
 </div>
 {game.players.map((player) => (
 <PlayerItem key={player.id} player={player} />
 ))}
 </div>

 
 {game.waitlist.length > 0 && (
 <div className="player-list">
 <div className="player-list-header">
 <span>Waitlist ({game.waitlist.length})</span>
 <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Position</span>
 </div>
 {game.waitlist.map((wp) => (
 <div key={wp.id} className="waitlist-item">
 <div className="waitlist-position">#{wp.position}</div>
 <div className="player-item-avatar">{wp.initials}</div>
 <div className="player-item-info">
 <div className="player-item-name">{wp.name}</div>
 <div className="player-item-role">In queue</div>
 </div>
 <span className="status-pill waiting">
 <span className="dot" />
 Waiting
 </span>
 </div>
 ))}
 </div>
 )}

 
 <div className="reliability-card" style={{ marginTop: '24px' }}>
 <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
 Game Details
 </h3>
 <div style={{ display: 'grid', gap: '12px', fontSize: '14px' }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
 <span style={{ color: 'var(--text-muted)' }}>Venue</span>
 <a
 href={getGoogleMapsUrl(game.playground)}
 target="_blank"
 rel="noopener noreferrer"
 style={{ color: 'var(--primary)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}
 title="Open venue in Google Maps"
 >
 {game.playground.name}
 <Icons.ExternalLink />
 </a>
 </div>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
 <span style={{ color: 'var(--text-muted)' }}>Address</span>
 <a
 href={getGoogleMapsUrl(game.playground)}
 target="_blank"
 rel="noopener noreferrer"
 style={{ color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '4px', textAlign: 'right', textDecoration: 'none' }}
 title="Open address in Google Maps"
 >
 {game.playground.address}
 <Icons.ExternalLink />
 </a>
 </div>
 <div style={{ display: 'flex', justifyContent: 'space-between' }}>
 <span style={{ color: 'var(--text-muted)' }}>Starts</span>
 <span>{formatTime(game.startsAt)}</span>
 </div>
 <div style={{ display: 'flex', justifyContent: 'space-between' }}>
 <span style={{ color: 'var(--text-muted)' }}>Ends</span>
 <span>{formatTime(game.endsAt)}</span>
 </div>
 <div style={{ display: 'flex', justifyContent: 'space-between' }}>
 <span style={{ color: 'var(--text-muted)' }}>Queue</span>
 <span style={{ color: 'var(--primary)' }}>Enabled</span>
 </div>
 </div>

 
 <a
 href={getGoogleMapsUrl(game.playground)}
 target="_blank"
 rel="noopener noreferrer"
 className="google-maps-btn"
 style={{ marginTop: '16px' }}
 title="Navigate to ground in Google Maps"
 >
 <span style={{ display: 'inline-flex', alignItems: 'center' }}><Icons.Compass /></span>
 <span>Open Playground in Google Maps</span>
 <span style={{ color: 'var(--primary)', marginLeft: 'auto', fontSize: '13px', fontWeight: 700 }}>Directions <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg></span>
 </a>

 {/* Private Event Match Chat Room */}
 <div className="event-chat-section" style={{ marginTop: '28px' }}>
   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
     <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
       <Icons.MessageSquare />
       Match Chat Room
     </h3>
     <span style={{
       fontSize: '11px',
       fontWeight: 600,
       padding: '3px 8px',
       borderRadius: '999px',
       background: hasChatAccess ? 'rgba(16, 185, 129, 0.12)' : 'rgba(100, 116, 139, 0.12)',
       color: hasChatAccess ? 'var(--status-success)' : 'var(--text-muted)',
     }}>
       {hasChatAccess ? (<span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor' }} /> Live Player Chat</span>) : (<span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Icons.Lock /> Joined Players Only</span>)}
     </span>
   </div>

   {!hasChatAccess ? (
     <div style={{
       background: 'var(--surface)',
       border: '1px dashed var(--border)',
       borderRadius: '16px',
       padding: '24px 20px',
       textAlign: 'center',
     }}>
       <div style={{
         width: '46px',
         height: '46px',
         borderRadius: '50%',
         background: 'rgba(124, 58, 237, 0.08)',
         color: 'var(--primary)',
         margin: '0 auto 12px',
         display: 'flex',
         alignItems: 'center',
         justifyContent: 'center',
       }}>
         <Icons.MessageSquare />
       </div>
       <h4 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 6px', color: 'var(--text)' }}>
         Private Match Chat Locked
       </h4>
       <p style={{ fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '380px', margin: '0 auto 16px', lineHeight: 1.5 }}>
         This chat room is private to players who have confirmed slots for {game.playground.name}. Join the event to chat with teammates, plan arrival, and share match updates.
       </p>
       <button
         className="join-btn"
         onClick={onJoin}
         disabled={isActionPending}
         style={{ padding: '8px 22px', fontSize: '13px' }}
       >
         {isActionPending ? 'Joining...' : 'Join Event to Unlock Chat'}
       </button>
     </div>
   ) : (
     <EventChatWidget
       eventId={game.id}
       authUser={authUser}
       gameName={game.playground.name}
     />
   )}

    {/* COMMUNITY SAFETY, REPORT & BLOCK ACTION BAR (PHASE 5) */}
    <div style={{
      marginTop: '20px',
      padding: '14px 18px',
      borderRadius: '14px',
      background: 'rgba(255, 255, 255, 0.02)',
      border: '1px solid var(--border)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '12px',
    }}>
      <div>
        <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text)' }}>
          Community Safety & Moderation
        </div>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
          Intermediary reporting & statutory grievance controls
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          onClick={() => {
            const reason = prompt('Please describe why you are reporting this match (e.g., incorrect venue, turf hazard, unauthorized fee):', 'Venue or match concern');
            if (reason) {
              fetch('/api/reports/game', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reporterId: authUser?.id || 'anonymous', gameId: game.id, reason }),
              }).catch(() => {});
              alert(`Report received for ${game.playground.name}. Added to moderation queue with high priority.`);
            }
          }}
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            padding: '6px 12px',
            fontSize: '11.5px',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          <Icons.Flag size={12} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }} /> Report Match
        </button>
        <button
          type="button"
          onClick={() => {
            const reason = prompt('Please describe the conduct issue regarding the host:', 'Misconduct or abusive communication');
            if (reason) {
              fetch('/api/reports/user', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reporterId: authUser?.id || 'anonymous', targetUserId: game.hostId || game.hostName, reason }),
              }).catch(() => {});
              alert(`Report received for host ${game.hostName}. Ticket issued and assigned to Grievance Officer.`);
            }
          }}
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            padding: '6px 12px',
            fontSize: '11.5px',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          <Icons.AlertTriangle size={12} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }} /> Report Host
        </button>
        <button
          type="button"
          onClick={() => {
            if (confirm(`Block ${game.hostName}? They will no longer be able to message you.`)) {
              fetch(`/api/block/${encodeURIComponent(game.hostId || game.hostName)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ currentUserId: authUser?.id, reason: 'User blocked' }),
              }).catch(() => {});
              alert(`Host ${game.hostName} is now blocked.`);
            }
          }}
          style={{
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            borderRadius: '8px',
            padding: '6px 12px',
            fontSize: '11.5px',
            fontWeight: 600,
            color: '#ef4444',
            cursor: 'pointer',
          }}
        >
          <Icons.Ban size={12} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }} /> Block Host
        </button>
      </div>
    </div>
 </div>
 </div>
 </div>
 );
}


function PlayerItem({ player }: { key?: React.Key; player: MockPlayer }) {
 return (
 <div className="player-item">
 <div className="player-item-avatar">{player.initials}</div>
 <div className="player-item-info">
 <div className="player-item-name">
 {player.name}
 {player.isHost && (
 <span style={{
 marginLeft: '6px', fontSize: '10px', fontWeight: 600,
 color: 'var(--status-waiting)', textTransform: 'uppercase',
 }}>
 Host
 </span>
 )}
 </div>
 <div className="player-item-role" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
 <span style={{
 color: player.reliability >= 90 ? 'var(--primary)' : player.reliability >= 75 ? 'var(--status-warning)' : 'var(--status-danger)',
 }}>
 {typeof player.reliability === 'number' && Number.isFinite(player.reliability) ? player.reliability : 100}% reliable
 </span>
 </div>
 </div>
 <span className={`status-pill ${getStatusPillClass(player.status)}`}>
 <span className="dot" />
 {getStatusLabel(player.status)}
 </span>
 </div>
 );
}


function ProfilePage({
 authUser,
 onSignOut,
 onRequireAuth,
}: {
 authUser: any;
 onSignOut?: () => void;
 onRequireAuth?: () => void;
}) {
 if (!authUser) {
  return (
   <div className="animate-fade-in" style={{ maxWidth: '600px', margin: '48px auto', padding: '32px 24px', textAlign: 'center' }}>
    <div style={{
     width: '72px',
     height: '72px',
     borderRadius: '50%',
     background: 'rgba(124, 58, 237, 0.1)',
     border: '2px solid rgba(124, 58, 237, 0.3)',
     display: 'flex',
     alignItems: 'center',
     justifyContent: 'center',
     margin: '0 auto 20px',
     color: 'var(--primary)',
    }}>
     <Icons.User />
    </div>
    <h2 style={{ fontSize: '24px', fontWeight: 800, marginBottom: '8px', color: 'var(--text-primary)' }}>
     You Are Signed Out
    </h2>
    <p style={{ fontSize: '14px', color: 'var(--text-muted)', maxWidth: '420px', margin: '0 auto 24px', lineHeight: 1.6 }}>
     Sign in or create an account to view your player profile, match history, reliability score, and favorite sports.
    </p>
    <button
     className="join-btn"
     onClick={onRequireAuth}
     style={{ padding: '10px 28px', fontSize: '14px', fontWeight: 700 }}
    >
     Sign In / Join
    </button>
   </div>
  );
 }

 const displayName = authUser.user_metadata?.full_name || authUser.name || authUser.email?.split('@')[0] || 'Player';
 const email = authUser.email || '';
 const initials = (displayName.slice(0, 2) || 'PL').toUpperCase();
 const ditchScore = authUser.ditchScore !== undefined ? authUser.ditchScore : 0;
 const reliability = authUser.checkInRate !== undefined ? Math.round(authUser.checkInRate * 100) : 100;
 const sports = authUser.sports || authUser.user_metadata?.sports || [];

 return (
 <div className="animate-fade-in" style={{ maxWidth: '750px', margin: '0 auto', padding: '24px 16px' }}>
 <div className="profile-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
 <div className="profile-avatar">{initials}</div>
 <div className="profile-info">
 <h2>{displayName}</h2>
 <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>{email}</div>
 <div className="profile-sports" style={{ marginTop: '8px' }}>
 {sports.map((s: string) => (
 <span key={s} className="profile-sport-tag">
 {s === 'CRICKET' ? '' : s === 'FOOTBALL' ? '' : ''} {s}
 </span>
 ))}
 </div>
 </div>
 </div>

 <div>
 {authUser ? (
 <button
 className="join-btn"
 onClick={onSignOut}
 style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#F87171', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '8px 16px', fontSize: '13px' }}
 >
 Sign Out
 </button>
 ) : (
 <button className="join-btn" onClick={onRequireAuth} style={{ padding: '8px 16px', fontSize: '13px' }}>
 Sign In / Join
 </button>
 )}
 </div>
 </div>

 <div style={{ marginTop: '24px' }}>
 <h3 className="section-title" style={{ marginBottom: '16px' }}>Reliability Score</h3>
 <div className="reliability-card">
 <div className="reliability-header">
 <span className="reliability-label"> Reliable Match Player</span>
 <span className="reliability-score">{reliability}%</span>
 </div>
 <div className="reliability-bar">
 <div
 className="reliability-bar-fill"
 style={{ width: `${reliability}%`, background: 'var(--brand-gradient, var(--primary))' }}
 />
 </div>
 <div className="reliability-stats">
 <div className="reliability-stat">
 <div className="value">{authUser.successfulGames ?? 0}</div>
 <div className="label">Completed</div>
 </div>
 <div className="reliability-stat">
 <div className="value">{Math.max(0, (authUser.totalGames ?? 0) - (authUser.successfulGames ?? 0))}</div>
 <div className="label">Cancelled</div>
 </div>
 <div className="reliability-stat">
 <div className="value">{authUser.noShows ?? 0}</div>
 <div className="label">No-shows</div>
 </div>
 </div>
 </div>

 <h3 className="section-title" style={{ marginTop: '24px', marginBottom: '16px' }}>Ditch Score</h3>
 <div className="reliability-card">
 <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
 <span style={{ fontSize: '28px', fontWeight: 800 }}>{ditchScore}</span>
 <span style={{ color: 'var(--primary)', fontSize: '14px', fontWeight: 600 }}>/ 100</span>
 </div>
 <div className="player-bar" style={{ height: '8px', marginBottom: '12px' }}>
 <div className="player-bar-fill" style={{ width: `${Math.min(ditchScore * 5, 100)}%`, background: 'var(--primary)' }} />
 </div>
 <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
 Lower is better. Score increases when you cancel late or don&apos;t show up. Complete scheduled games to reduce your score.
 </p>
 </div>

 <h3 className="section-title" style={{ marginTop: '24px', marginBottom: '16px' }}>Recent Match Activity</h3>
        {authUser?.matchHistory && authUser.matchHistory.length > 0 ? (
          <div className="player-list">
            {authUser.matchHistory.map((g: any, i: number) => (
              <div key={i} className="player-item">
                <div style={{ width: '36px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <Icons.Calendar />
                </div>
                <div className="player-item-info">
                  <div className="player-item-name">{g.name}</div>
                  <div className="player-item-role">{g.date}</div>
                </div>
                <span className={`status-pill ${g.status || 'checked-in'}`}>
                  <span className="dot" />
                  {g.result || 'Completed'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '36px 16px',
            background: 'var(--surface)',
            borderRadius: 'var(--radius-lg)',
            border: '1px dashed var(--border)',
          }}>
            <div style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px',
              color: 'var(--text-muted)',
            }}>
              <Icons.Calendar />
            </div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>
              No Match History Yet
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', maxWidth: '340px', margin: '0 auto' }}>
              Join games or host matches in your district to record verified play stats.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MyGamesPage({
 games,
 joinedGames,
 authUser,
 onSelectGame,
 onOpenCreate,
 onRequireAuth,
}: {
 games: MockGame[];
 joinedGames: Set<string>;
 authUser: any;
 onSelectGame: (g: MockGame) => void;
 onOpenCreate: () => void;
 onRequireAuth?: () => void;
}) {
 if (!authUser) {
  return (
   <div style={{ padding: '48px 24px', maxWidth: '600px', margin: '0 auto', width: '100%', textAlign: 'center' }}>
    <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '26px', letterSpacing: '-0.02em', marginBottom: '8px' }}>
     My Games
    </h2>
    <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '24px' }}>
     Sign in to view games you are hosting or have claimed a slot for.
    </p>
    <button className="join-btn" onClick={onRequireAuth || onOpenCreate} style={{ padding: '10px 24px', fontSize: '14px' }}>
     Sign In / Join
    </button>
   </div>
  );
 }

 const currentUserId = authUser.id;
 const userPrefix = authUser.email?.split('@')[0]?.toLowerCase();

 const myGames = games.filter(g =>
 joinedGames.has(g.id) ||
 g.players?.some(p => p.id === currentUserId || (userPrefix && p.name.toLowerCase().includes(userPrefix))) ||
 (userPrefix && g.hostName && g.hostName.toLowerCase().includes(userPrefix))
 );

 return (
 <div style={{ padding: '32px 24px', maxWidth: '900px', margin: '0 auto', width: '100%' }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
 <div>
 <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '26px', letterSpacing: '-0.02em' }}>My Games</h2>
 <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginTop: '4px' }}>
 Games you are hosting or have claimed a slot for
 </p>
 </div>
 <button className="join-btn" onClick={onOpenCreate} style={{ padding: '8px 16px', fontSize: '13px' }}>
 + Create New
 </button>
 </div>

 {myGames.length === 0 ? (
 <div style={{
 textAlign: 'center',
 padding: '60px 20px',
 background: 'var(--surface)',
 borderRadius: 'var(--radius-lg)',
 border: '1px dashed var(--border)',
 }}>
 <div style={{ fontSize: '48px', marginBottom: '16px' }}></div>
 <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>No active games yet</h3>
 <p style={{ color: 'var(--text-muted)', fontSize: '14px', maxWidth: '400px', margin: '0 auto 20px' }}>
 Join an open slot nearby or create your own game and invite teammates!
 </p>
 <button className="join-btn" onClick={onOpenCreate}>
 Host a Game Now
 </button>
 </div>
 ) : (
 <div className="games-grid">
 {myGames.map(game => (
 <div
 key={game.id}
 className="game-card animate-fade-in"
 onClick={() => onSelectGame(game)}
 style={{ cursor: 'pointer' }}
 >
 <div className="game-card-image">
 <div style={{
 width: '100%', height: '100%',
 background: 'linear-gradient(135deg, var(--elevated), var(--surface))',
 display: 'flex', alignItems: 'center', justifyContent: 'center',
 fontSize: '28px', opacity: 0.7,
 }}>
 {game.sportEmoji}
 </div>
 <div className="game-card-sport-badge">
 <span>{game.sportEmoji}</span>
 {game.sport}
 </div>
 </div>
 <div className="game-card-body">
 <div className="game-card-title">{game.title}</div>
 <div className="game-card-meta">
 <Icons.MapPin />
 {game.playground.name}
 <span className="dot" />
 Starts {formatTime(game.startsAt)}
 </div>
 <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
 <span className="status-pill open">
 <span className="dot" />
 {game.confirmedPlayers}/{game.capacity} Confirmed
 </span>
 <span style={{ fontSize: '13px', color: 'var(--primary)', fontWeight: 600 }}>
 View Room →
 </span>
 </div>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 );
}


function EventChatWidget({
  eventId,
  authUser,
  gameName,
}: {
  eventId: string;
  authUser: any;
  gameName: string;
}) {
  const currentUserId = authUser?.id || authUser?.email || '';
  const [messages, setMessages] = useState<Array<{ id: string; sender: string; text: string; time: string; isSelf: boolean; isSystem?: boolean }>>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchEventMessagesApi(eventId, currentUserId).then(res => {
      if (!isMounted) return;
      setLoading(false);
      if (!res.isJoined) {
        setError(res.error || 'Only players who joined this event can view this chat room.');
      } else {
        setMessages(res.messages || []);
      }
    }).catch(err => {
      if (!isMounted) return;
      setLoading(false);
      setError(err.message || 'Failed to load chat');
    });

    const socket = getGameSocket();
    socket.emit('join-room', { gameId: eventId, userId: currentUserId });

    const handleIncoming = (incoming: any) => {
      if (!incoming) return;
      setMessages(prev => {
        if (prev.some(m => m.id === incoming.id)) return prev;
        return [...prev, incoming];
      });
    };

    const handleEvent = (payload: any) => {
      if (payload?.event === 'CHAT_MESSAGE' && payload.data?.message && payload.gameId === eventId) {
        handleIncoming(payload.data.message);
      }
    };

    socket.on('chat-message', handleIncoming);
    socket.on('game-event', handleEvent);

    return () => {
      isMounted = false;
      socket.off('chat-message', handleIncoming);
      socket.off('game-event', handleEvent);
      socket.emit('leave-room', { gameId: eventId });
    };
  }, [eventId, currentUserId]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim()) return;
    const textToSend = draft.trim();
    setDraft('');

    const senderName = authUser?.user_metadata?.full_name || authUser?.name || authUser?.email?.split('@')[0] || 'You';
    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      sender: senderName,
      text: textToSend,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isSelf: true,
    };
    setMessages(prev => [...prev, optimisticMsg]);

    try {
      await sendEventMessageApi(eventId, currentUserId, textToSend, senderName, authUser?.user_metadata?.avatar_url);
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
    }
  };

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: '16px',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '340px',
    }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--elevated)' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>
          {gameName} Match Room
        </div>
        <span style={{ fontSize: '11px', color: 'var(--status-success)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--status-success)' }}></span>
          Confirmed Players Only
        </span>
      </div>

      <div style={{ flex: 1, padding: '14px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {loading ? (
          <div style={{ margin: 'auto', color: 'var(--text-muted)', fontSize: '13px' }}>Loading match chat...</div>
        ) : error ? (
          <div style={{ margin: 'auto', color: 'var(--danger)', fontSize: '13px', padding: '10px 14px', background: 'rgba(239, 68, 68, 0.08)', borderRadius: '8px' }}>
            {error}
          </div>
        ) : messages.length === 0 ? (
          <div style={{ margin: 'auto', color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center' }}>
            No messages yet. Say hello to your teammates!
          </div>
        ) : (
          messages.map(m => (
            <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: m.isSystem ? 'center' : m.isSelf ? 'flex-end' : 'flex-start' }}>
              {m.isSystem ? (
                <div style={{
                  fontSize: '11px',
                  color: 'var(--text-secondary)',
                  background: 'var(--elevated)',
                  border: '1px solid var(--border)',
                  padding: '4px 10px',
                  borderRadius: '999px',
                  margin: '4px 0',
                }}>
                  {m.text}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '2px' }}>
                    {m.sender} • {m.time}
                  </div>
                  <div style={{
                    maxWidth: '82%',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    background: m.isSelf ? 'var(--primary)' : 'var(--elevated)',
                    color: m.isSelf ? '#ffffff' : 'var(--text)',
                    fontSize: '13px',
                    lineHeight: 1.4,
                    border: m.isSelf ? 'none' : '1px solid var(--border)',
                  }}>
                    {m.text}
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      <form onSubmit={handleSend} style={{ display: 'flex', padding: '10px', gap: '8px', borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
        <input
          className="form-input"
          style={{ height: '38px', fontSize: '13px' }}
          placeholder="Message teammates in this event..."
          value={draft}
          onChange={e => setDraft(e.target.value)}
        />
        <button
          type="submit"
          className="join-btn"
          disabled={!draft.trim()}
          style={{ height: '38px', padding: '0 16px', fontSize: '13px', whiteSpace: 'nowrap', marginTop: 0, width: 'auto' }}
        >
          Send
        </button>
      </form>
    </div>
  );
}

function MessagesPage({
  games,
  joinedGames,
  authUser,
  onRequireAuth,
  onExploreGames,
  onSelectGame,
}: {
  games: MockGame[];
  joinedGames: Set<string>;
  authUser: any;
  onRequireAuth?: () => void;
  onExploreGames?: () => void;
  onSelectGame?: (game: MockGame) => void;
}) {
  const currentUserId = authUser?.id || (authUser as any)?._id || authUser?.email || '';

  const [backendJoinedGames, setBackendJoinedGames] = useState<MockGame[]>([]);

  useEffect(() => {
    if (!currentUserId) return;
    fetchUserJoinedEventsApi(currentUserId).then((events: any[]) => {
      if (Array.isArray(events)) {
        const mapped: MockGame[] = events.map(e => ({
          id: e.id || e._id,
          title: e.title || `${e.playground?.name || 'Local Ground'} ${e.sport || 'Match'}`,
          sport: e.sport || 'BADMINTON',
          sportEmoji: e.sportEmoji || '',
          playground: {
            name: e.playground?.name || 'Local Ground',
            address: e.playground?.address || '',
            lat: e.playground?.lat ?? (e.playground?.location?.coordinates?.[1] || 28.6139),
            lng: e.playground?.lng ?? (e.playground?.location?.coordinates?.[0] || 77.2090),
          },
          capacity: e.capacity || 10,
          confirmedPlayers: e.confirmedPlayers || 1,
          neededPlayers: Math.max(0, (e.capacity || 10) - (e.confirmedPlayers || 1)),
          status: e.status || 'OPEN',
          startsAt: e.startsAt || new Date().toISOString(),
          endsAt: e.endsAt || new Date().toISOString(),
          distance: 500,
          isLive: true,
          hostId: e.hostId,
          hostName: e.hostName || 'Host',
          players: e.players || [],
          waitlist: [],
        }));
        setBackendJoinedGames(mapped);
      }
    }).catch(() => {});
  }, [currentUserId]);

  const allAvailableGames = useMemo(() => {
    const map = new Map<string, MockGame>();
    games.forEach(g => map.set(g.id, g));
    backendJoinedGames.forEach(g => {
      if (!map.has(g.id)) map.set(g.id, g);
    });
    return Array.from(map.values());
  }, [games, backendJoinedGames]);

  // Filter ONLY events that the user has joined or created
  const userJoinedGames = useMemo(() => {
    if (!authUser) return [];
    return allAvailableGames.filter(g =>
      joinedGames.has(g.id) ||
      isUserHostOfGame(g, authUser) ||
      isUserPlayerInGame(g, authUser, joinedGames)
    );
  }, [allAvailableGames, joinedGames, authUser]);

  const [selectedGameId, setSelectedGameId] = useState<string>(userJoinedGames[0]?.id || '');
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [messages, setMessages] = useState<Record<string, Array<{ id: string; sender: string; text: string; time: string; isSelf: boolean; isSystem?: boolean }>>>({});
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (userJoinedGames.length > 0 && (!selectedGameId || !userJoinedGames.some(g => g.id === selectedGameId))) {
      setSelectedGameId(userJoinedGames[0].id);
    }
  }, [userJoinedGames, selectedGameId]);

  useEffect(() => {
    if (!selectedGameId || !authUser) return;
    let isCancelled = false;
    setLoading(true);
    setErrorMsg(null);

    fetchEventMessagesApi(selectedGameId, currentUserId).then(res => {
      if (isCancelled) return;
      setLoading(false);
      if (!res.isJoined) {
        setErrorMsg(res.error || 'Only players who joined this event can view this chat room.');
      } else {
        setMessages(prev => ({
          ...prev,
          [selectedGameId]: res.messages,
        }));
      }
    }).catch(err => {
      if (isCancelled) return;
      setLoading(false);
      setErrorMsg(err.message || 'Failed to load messages');
    });

    const socket = getGameSocket();
    socket.emit('join-room', { gameId: selectedGameId, userId: currentUserId });

    const handleIncomingMessage = (incoming: any) => {
      if (!incoming) return;
      setMessages(prev => {
        const existing = prev[selectedGameId] || [];
        if (existing.some(m => m.id === incoming.id)) return prev;
        return {
          ...prev,
          [selectedGameId]: [...existing, incoming],
        };
      });
    };

    const handleGameEvent = (payload: any) => {
      if (payload?.event === 'CHAT_MESSAGE' && payload.data?.message && payload.gameId === selectedGameId) {
        handleIncomingMessage(payload.data.message);
      }
    };

    socket.on('chat-message', handleIncomingMessage);
    socket.on('game-event', handleGameEvent);

    return () => {
      isCancelled = true;
      socket.off('chat-message', handleIncomingMessage);
      socket.off('game-event', handleGameEvent);
      socket.emit('leave-room', { gameId: selectedGameId });
    };
  }, [selectedGameId, authUser, currentUserId]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim() || !selectedGameId) return;
    const textToSend = draft.trim();
    setDraft('');

    const senderDisplayName = authUser?.user_metadata?.full_name || authUser?.name || authUser?.email?.split('@')[0] || 'You';
    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      sender: senderDisplayName,
      text: textToSend,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isSelf: true,
    };

    setMessages(prev => ({
      ...prev,
      [selectedGameId]: [...(prev[selectedGameId] || []), optimisticMsg],
    }));

    try {
      await sendEventMessageApi(selectedGameId, currentUserId, textToSend, senderDisplayName, authUser?.user_metadata?.avatar_url);
    } catch (err: any) {
      console.warn('Failed to send event message:', err);
    }
  };

  const activeGame = userJoinedGames.find(g => g.id === selectedGameId) || userJoinedGames[0];
  const currentMessages = messages[selectedGameId] || [];

  if (!authUser) {
    return (
      <div style={{ padding: '40px 20px', maxWidth: '520px', margin: '60px auto', textAlign: 'center' }}>
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '24px',
          padding: '40px 28px',
          boxShadow: 'var(--shadow-card)',
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'rgba(124, 58, 237, 0.1)',
            color: 'var(--accent)',
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Icons.MessageSquare />
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '24px', fontWeight: 800, margin: '0 0 10px', color: 'var(--text)' }}>
            Sign In to Access Event Chats
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.5, margin: '0 0 28px' }}>
            Event chat rooms are private to confirmed players. Coordinate matches, kit, and arrival directly with teammates.
          </p>
          <button
            onClick={() => onRequireAuth?.()}
            className="btn-primary"
            style={{ width: '100%', height: '46px', fontSize: '15px' }}
          >
            Sign In or Sign Up
          </button>
        </div>
      </div>
    );
  }

  if (userJoinedGames.length === 0) {
    return (
      <div style={{ padding: '60px 20px', maxWidth: '520px', margin: '0 auto', textAlign: 'center' }}>
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '24px',
          padding: '48px 28px',
          boxShadow: 'var(--shadow-card)',
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'rgba(124, 58, 237, 0.1)',
            color: 'var(--primary)',
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Icons.MessageSquare />
          </div>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: 800, margin: '0 0 10px', color: 'var(--text)' }}>
            No Joined Event Chats Yet
          </h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.6, margin: '0 0 28px' }}>
            Event chat rooms are strictly private to players who have confirmed their slots. Join an upcoming sports event near you or create your own to unlock the live team chat!
          </p>
          <button
            onClick={onExploreGames}
            className="join-btn"
            style={{ width: '100%', height: '46px', fontSize: '15px' }}
          >
            Find Nearby Games to Join
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="messages-layout-container">
      {/* Chats List Sidebar: Shows ONLY Joined/Hosted Events */}
      <div className={`messages-sidebar ${showMobileChat ? 'hide-mobile' : ''}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 16px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: 'var(--text)' }}>Joined Event Chats</h3>
          <span style={{ fontSize: '11px', background: 'rgba(124, 58, 237, 0.12)', color: 'var(--primary)', padding: '2px 8px', borderRadius: '999px', fontWeight: 600 }}>
            {userJoinedGames.length} Active
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto' }}>
          {userJoinedGames.map(g => (
            <button
              key={g.id}
              onClick={() => {
                setSelectedGameId(g.id);
                setShowMobileChat(true);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '12px 14px', borderRadius: 'var(--radius-md)',
                background: selectedGameId === g.id ? 'var(--elevated)' : 'transparent',
                border: selectedGameId === g.id ? '1px solid var(--border)' : '1px solid transparent',
                color: 'var(--text-primary)', textAlign: 'left', cursor: 'pointer',
                transition: 'background 0.15s ease',
              }}
            >
              <SportAvatarBadge sport={g.sport} />
              <div style={{ overflow: 'hidden', flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                  {g.playground.name}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{g.sport} • {g.confirmedPlayers} players</div>
              </div>
              <Icons.ChevronRight />
            </button>
          ))}
        </div>
      </div>

      {/* Chat Conversation Panel */}
      <div className={`messages-chat-panel ${!showMobileChat ? 'hide-mobile' : ''}`}>
        {/* Top Header */}
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => setShowMobileChat(false)}
            className="messages-back-btn"
            aria-label="Back to chat list"
          >
            <Icons.ArrowRight style={{ transform: 'rotate(180deg)' }} />
          </button>
          <SportAvatarBadge sport={activeGame?.sport} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {activeGame?.playground.name || 'Event Match Chat'}
            </h4>
            <span style={{ fontSize: '12px', color: 'var(--status-success)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--status-success)' }}></span>
              Joined Players Chat • {activeGame?.confirmedPlayers || 1} confirmed
            </span>
          </div>
        </div>

        {/* Message Thread */}
        <div style={{ flex: 1, padding: '16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {loading ? (
            <div style={{ margin: 'auto', color: 'var(--text-muted)', fontSize: '13px' }}>Loading messages...</div>
          ) : errorMsg ? (
            <div style={{ margin: 'auto', color: 'var(--danger)', fontSize: '13px', background: 'rgba(239, 68, 68, 0.08)', padding: '10px 14px', borderRadius: '8px' }}>
              {errorMsg}
            </div>
          ) : currentMessages.length === 0 ? (
            <div style={{ margin: 'auto', color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center' }}>
              No messages yet in this match chat. Say hello to your teammates!
            </div>
          ) : (
            currentMessages.map(m => (
              <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: m.isSystem ? 'center' : m.isSelf ? 'flex-end' : 'flex-start' }}>
                {m.isSystem ? (
                  <div style={{
                    fontSize: '11px',
                    color: 'var(--text-secondary)',
                    background: 'var(--elevated)',
                    border: '1px solid var(--border)',
                    padding: '4px 12px',
                    borderRadius: '999px',
                    margin: '4px 0',
                  }}>
                    {m.text}
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '3px' }}>
                      {m.sender} • {m.time}
                    </div>
                    <div style={{
                      maxWidth: '82%', padding: '10px 14px', borderRadius: '14px',
                      background: m.isSelf ? 'var(--primary)' : 'var(--elevated)',
                      color: m.isSelf ? '#ffffff' : 'var(--text-primary)',
                      fontWeight: m.isSelf ? 500 : 400,
                      fontSize: '14px',
                      border: m.isSelf ? 'none' : '1px solid var(--border)',
                      lineHeight: 1.4,
                    }}>
                      {m.text}
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        {/* Message Input */}
        <form onSubmit={handleSend} style={{ padding: '14px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: '10px', background: 'var(--surface)' }}>
          <input
            className="form-input"
            placeholder="Type a message to teammates..."
            value={draft}
            onChange={e => setDraft(e.target.value)}
            style={{
              flex: 1,
              padding: '10px 14px',
              borderRadius: '10px',
              border: '1px solid var(--border)',
              background: 'var(--elevated)',
              fontSize: '14px',
              outline: 'none',
            }}
          />
          <button className="join-btn" type="submit" disabled={!draft.trim()} style={{ padding: '0 20px', height: '42px', marginTop: 0, width: 'auto' }}>
            Send
          </button>
        </form>
      </div>
    </div>
  );
}


function NotificationsPage({
  authUser,
  selectedDistrict,
  onSelectGameId,
  onUnreadCountChange,
}: {
  authUser: any;
  selectedDistrict?: District | null;
  onSelectGameId?: (gameId: string) => void;
  onUnreadCountChange?: (count: number) => void;
}) {
  const currentUserId = authUser?.id || (authUser as any)?._id || authUser?.email || '';
  const [notifications, setNotifications] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadNotifications = useCallback(async () => {
    if (!currentUserId) {
      setNotifications([]);
      return;
    }
    setIsLoading(true);
    try {
      const items = await fetchNotificationsApi(currentUserId, selectedDistrict?.slug);
      if (Array.isArray(items)) {
        setNotifications(items);
        const unread = items.filter(n => n.isUnread).length;
        if (onUnreadCountChange) onUnreadCountChange(unread);
      }
    } catch {
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, selectedDistrict, onUnreadCountChange]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const markAllRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, isUnread: false })));
    if (onUnreadCountChange) onUnreadCountChange(0);
    if (currentUserId) {
      try {
        await markNotificationsReadApi(currentUserId);
      } catch {}
    }
  };

  return (
    <div style={{ padding: '32px 24px', maxWidth: '800px', margin: '0 auto', width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '24px', letterSpacing: '-0.02em' }}>Notifications</h2>
        <button onClick={markAllRead} style={{ fontSize: 'var(--text-sm)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
          Mark all as read
        </button>
      </div>

      {typeof window !== 'undefined' && 'Notification' in window && Notification.permission !== 'granted' && (
        <div style={{
          background: 'var(--surface, #ffffff)',
          border: '1px solid var(--border, #e8e8ed)',
          borderRadius: '16px',
          padding: '16px 20px',
          marginBottom: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '14px',
          flexWrap: 'wrap',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(124, 58, 237, 0.12)',
              color: '#7c3aed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Icons.Bell />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text, #0a0a0b)' }}>
                Browser Notifications are disabled
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #6b6b76)', marginTop: '2px' }}>
                Allow browser alerts to receive instant match invites and slot confirmations.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={async () => {
              await requestNotificationPermission();
            }}
            style={{
              background: '#0a0a0b',
              color: '#ffffff',
              border: 'none',
              borderRadius: '999px',
              padding: '8px 18px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            Enable Notifications
          </button>
        </div>
      )}
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {notifications.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '50px 20px',
            background: 'var(--surface)',
            borderRadius: 'var(--radius-lg)',
            border: '1px dashed var(--border)',
          }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px',
              color: 'var(--text-muted)',
            }}>
              <Icons.Bell />
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 6px' }}>No notifications yet</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '13px', maxWidth: '360px', margin: '0 auto' }}>
              You will receive match alerts and join requests here once matches are created in {selectedDistrict?.name || 'your zone'}.
            </p>
          </div>
        ) : (
          notifications.map(n => (
            <div 
              key={n.id} 
              style={{ 
                padding: '16px', 
                borderRadius: 'var(--radius-lg)', 
                background: n.isUnread ? 'var(--surface-elevated)' : 'var(--surface)',
                border: '1px solid var(--border)',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <strong style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px' }}>
                  {n.isUnread && <span style={{ width: '8px', height: '8px', background: 'var(--primary)', borderRadius: '50%' }} />}
                  {n.title}
                </strong>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{n.time}</span>
              </div>
              <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', paddingLeft: n.isUnread ? '16px' : '0' }}>{n.message}</p>
              {n.gameId && onSelectGameId && (
                <button
                  type="button"
                  onClick={() => onSelectGameId(n.gameId)}
                  style={{
                    alignSelf: 'flex-start',
                    marginTop: '8px',
                    marginLeft: n.isUnread ? '16px' : '0',
                    padding: '6px 16px',
                    borderRadius: '20px',
                    background: 'var(--primary)',
                    color: '#ffffff',
                    border: 'none',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <Icons.Zap size={13} style={{ flexShrink: 0 }} />
                    <span>View Match &amp; Join Now</span>
                    <Icons.ArrowRight />
                  </span>
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function CreateGameModal({
  onClose,
  onCreated,
  authUser,
  onRequireAuth,
  userLocation,
  selectedDistrict,
}: {
  onClose: () => void;
  onCreated: (newGame: MockGame, msg: string) => void;
  authUser: any;
  onRequireAuth: () => void;
  userLocation?: { lat: number; lng: number; name: string; isLive: boolean } | null;
  selectedDistrict?: District | null;
}) {
  if (!authUser) {
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div
          className="modal-content"
          style={{ maxWidth: '440px', padding: '32px 24px', textAlign: 'center' }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{
            width: '52px',
            height: '52px',
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            color: '#FFFFFF',
          }}>
            <Icons.Lock />
          </div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, margin: '0 0 8px', color: 'var(--text)' }}>
            Sign In Required to Create Games
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '0 0 24px', lineHeight: 1.5 }}>
            You must be logged in to create games, reserve turf lobbies, and invite local players to your matches.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              type="button"
              className="join-btn"
              onClick={() => {
                onClose();
                onRequireAuth();
              }}
              style={{ width: '100%', height: '42px', fontSize: '14px', fontWeight: 700 }}
            >
              Sign In / Register
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                width: '100%',
                height: '38px',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                color: 'var(--text-secondary)',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active coordinates: Use user's real location or district, fallback to central Delhi (28.6139, 77.2090)
  const defaultLat = userLocation?.lat ?? selectedDistrict?.lat ?? 28.6139;
  const defaultLng = userLocation?.lng ?? selectedDistrict?.lng ?? 77.2090;

  // Active address: Use user's real area or district name (clear legacy Bangalore demo string)
  const defaultAddress = userLocation?.name && !userLocation.name.toLowerCase().includes('koramangala')
    ? userLocation.name
    : (selectedDistrict ? `${selectedDistrict.name}, ${selectedDistrict.state}` : '');

  const districtLabel = selectedDistrict?.name || (userLocation?.name?.split(',')[0]) || '';

  const [sport, setSport] = useState('CRICKET');
  const [playgroundName, setPlaygroundName] = useState('');
  const [address, setAddress] = useState(defaultAddress);
  const [lat, setLat] = useState(defaultLat);
  const [lng, setLng] = useState(defaultLng);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationSuccessMsg, setLocationSuccessMsg] = useState('');
  const [capacity, setCapacity] = useState(10);
  const [skillLevel, setSkillLevel] = useState('MIXED');

  // Strictly calculate the 7-day event booking window (today through today + 6 days)
  const calendarDays = useMemo(() => {
    const days = [];
    const base = new Date();
    for (let i = 0; i < 7; i++) {
      const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const date = String(d.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${date}`;
      const weekday = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : d.toLocaleDateString('en-US', { weekday: 'short' });
      const dayNum = d.getDate();
      const monthShort = d.toLocaleDateString('en-US', { month: 'short' });
      const fullLabel = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

      days.push({
        dateStr,
        weekday,
        dayNum,
        monthShort,
        fullLabel,
        isToday: i === 0,
      });
    }
    return days;
  }, []);

  const minDateStr = calendarDays[0]?.dateStr || '';
  const maxDateStr = calendarDays[calendarDays.length - 1]?.dateStr || '';
  const [eventDate, setEventDate] = useState(() => minDateStr);

  const [startTime, setStartTime] = useState('18:00');
  const [endTime, setEndTime] = useState('20:00');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Dynamically calculate nearby arenas & stadiums for the user's current coordinates & district
  const nearbyArenas = useMemo(() => {
    return getNearbyVenues(lat, lng, districtLabel || address);
  }, [lat, lng, districtLabel, address]);

  const sports = [
    { key: 'CRICKET', name: 'Cricket' },
    { key: 'FOOTBALL', name: 'Football' },
    { key: 'BASKETBALL', name: 'Basketball' },
    { key: 'BADMINTON', name: 'Badminton' },
    { key: 'TENNIS', name: 'Tennis' },
    { key: 'VOLLEYBALL', name: 'Volleyball' },
    { key: 'HOCKEY', name: 'Hockey' },
    { key: 'TABLE_TENNIS', name: 'TT' },
    { key: 'OTHER', name: '+ Other' },
  ];
  const [customSport, setCustomSport] = useState('');

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setErrorMsg('Geolocation is not supported by your browser');
      return;
    }
    setIsLocating(true);
    setLocationSuccessMsg('');
    setErrorMsg('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const userLat = Number(pos.coords.latitude.toFixed(6));
        const userLng = Number(pos.coords.longitude.toFixed(6));
        setLat(userLat);
        setLng(userLng);
        setIsLocating(false);
        setLocationSuccessMsg(`Locked to current location: ${userLat}, ${userLng}`);

        try {
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${userLat}&lon=${userLng}`);
          if (res.ok) {
            const data = await res.json();
            if (data.display_name) {
              const parts = data.display_name.split(',').map((s: string) => s.trim());
              setAddress(parts.slice(0, 3).join(', '));
            }
          }
        } catch {}
      },
      (err) => {
        setIsLocating(false);
        setErrorMsg('Unable to retrieve current location: ' + err.message);
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

 const handleSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 if (!playgroundName.trim()) {
 setErrorMsg('Please enter a playground or venue name');
 return;
 }

 if (!eventDate || eventDate < minDateStr || eventDate > maxDateStr) {
 setErrorMsg(`Event date must be within future 7 days (${minDateStr} to ${maxDateStr})`);
 return;
 }

 setSubmitting(true);
 setErrorMsg('');

 try {
 const [year, month, day] = eventDate.split('-').map(Number);
 const [startH, startM] = startTime.split(':').map(Number);
 const [endH, endM] = endTime.split(':').map(Number);

 const startsAt = new Date(year, (month || 1) - 1, day || 1, startH || 18, startM || 0, 0, 0);
 const now = new Date();

 if (eventDate === minDateStr && startsAt.getTime() < now.getTime() - 2 * 60 * 1000) {
 setErrorMsg('Start time for today cannot be in the past. Please select an upcoming time.');
 setSubmitting(false);
 return;
 }

 const endsAt = new Date(year, (month || 1) - 1, day || 1, endH || 20, endM || 0, 0, 0);
 if (endsAt.getTime() <= startsAt.getTime()) {
 endsAt.setDate(endsAt.getDate() + 1);
 }

 if (!authUser) {
 setErrorMsg('Please sign in to create a game');
 onRequireAuth();
 setSubmitting(false);
 return;
 }

 if (sport === 'OTHER' && !customSport.trim()) {
      setErrorMsg('Please specify your custom sport or game name');
      setSubmitting(false);
      return;
    }

    const effectiveSport = sport === 'OTHER' ? (customSport.trim().toUpperCase() || 'OTHER') : sport;
    const effectiveSportLabel = sport === 'OTHER' ? customSport.trim() : (sports.find(s => s.key === sport)?.name || sport);

    const hostId = authUser.id || authUser.email;
    const idemKey = generateIdempotencyKey(`host_game_${hostId}`);

    const res = await createGameApi({
      hostId,
      sport: effectiveSport,
 playground: {
 name: playgroundName.trim(),
 address: address.trim(),
 lat,
 lng,
 },
 capacity: Number(capacity) || 10,
 startsAt: startsAt.toISOString(),
 endsAt: endsAt.toISOString(),
 description: description.trim(),
 skillLevel,
 districtSlug: selectedDistrict?.slug,
 districtName: selectedDistrict?.name,
 idempotencyKey: idemKey,
 });

 const sportObj = sports.find(s => s.key === sport);
 const hostDisplayName = authUser?.user_metadata?.full_name || authUser?.name || authUser?.email?.split('@')[0] || 'You (Host)';

 const newMockGame: MockGame = {
      id: res._id || `game-${Date.now()}`,
      sportEmoji: '',
 title: `${playgroundName} ${typeof effectiveSportLabel !== 'undefined' ? effectiveSportLabel : sport}`,
      sport: typeof effectiveSport !== 'undefined' ? effectiveSport : sport,
 playground: {
 name: playgroundName,
 address,
 lat: res.playground?.location?.coordinates?.[1] || lat,
 lng: res.playground?.location?.coordinates?.[0] || lng,
 },
 capacity: Number(capacity) || 10,
 confirmedPlayers: 1,
 neededPlayers: Math.max(0, (Number(capacity) || 10) - 1),
 status: 'OPEN',
 startsAt: startsAt.toISOString(),
 endsAt: endsAt.toISOString(),
 distance: 800,
 isLive: true,
 hostId: hostId,
 hostName: hostDisplayName,
 players: [
 {
 id: hostId,
 name: hostDisplayName,
 initials: hostDisplayName.slice(0, 2).toUpperCase(),
 status: 'confirmed',
 isHost: true,
 reliability: authUser?.checkInRate !== undefined ? Math.round(authUser.checkInRate * 100) : 100,
 },
 ],
 waitlist: [],
 };

 onCreated(newMockGame, `Game "${playgroundName}" created! `);
 onClose();
 } catch (err: any) {
 setErrorMsg(err.message || 'Failed to create game on backend');
 } finally {
 setSubmitting(false);
 }
 };

 return (
 <div className="create-game-modal-overlay" onClick={onClose}>
 <div className="create-game-modal" style={{ maxWidth: '520px', textAlign: 'left' }} onClick={e => e.stopPropagation()}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
 <div>
 <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
 <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', margin: 0 }}>Create Sports Event</h3>
 <button
 type="button"
 onClick={() => {
   if (typeof window !== 'undefined') {
     window.dispatchEvent(new CustomEvent('kheldrop_open_guide', { detail: { mode: 'host' } }));
   }
 }}
 style={{
   fontSize: '11px',
   fontWeight: 700,
   background: 'rgba(124, 58, 237, 0.1)',
   color: '#7c3aed',
   border: '1px solid rgba(124, 58, 237, 0.3)',
   padding: '2px 8px',
   borderRadius: '999px',
   cursor: 'pointer',
   display: 'inline-flex',
   alignItems: 'center',
   gap: '4px',
 }}
 title="View Host Step-by-Step Guide"
 >
 <span>Guide</span>
 </button>
 </div>
 <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: '2px 0 0' }}>Post a game for nearby players to join</p>
 </div>
 <button onClick={onClose} style={{ color: 'var(--text-muted)', padding: '4px', background: 'none', border: 'none', cursor: 'pointer' }}>
 <Icons.X />
 </button>
 </div>

 {errorMsg && (
 <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid var(--danger)', color: '#F87171', padding: '10px 14px', borderRadius: '8px', fontSize: 'var(--text-xs)', marginBottom: '16px' }}>
 {errorMsg}
 </div>
 )}

 <form onSubmit={handleSubmit}>
 <div className="form-group">
 <label className="form-label">Sport</label>
 <div className="sport-selector">
 {sports.map(s => (
              <button
                type="button"
                key={s.key}
                className={`sport-option ${sport === s.key ? 'selected' : ''}`}
                onClick={() => setSport(s.key)}
              >
                <span className="emoji" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <SportIcon sport={s.key} size={20} color={sport === s.key ? 'var(--primary, #8B5CF6)' : 'currentColor'} />
                </span>
                <span className="name">{s.name}</span>
              </button>
            ))}
          </div>
          {sport === 'OTHER' && (
            <div style={{ marginTop: '12px' }}>
              <label className="form-label">Specify Custom Sport / Game</label>
              <input
                className="form-input"
                placeholder="e.g. Pickleball, Kho-Kho, Kabaddi, Chess, Frisbee, Padel, Rugby..."
                value={customSport}
                onChange={e => setCustomSport(e.target.value)}
                required
                autoFocus
              />
            </div>
          )}
        </div>

 <div className="form-group">
 <label className="form-label">Playground / Turf Name</label>
 <input
 className="form-input"
 placeholder={nearbyArenas[0] ? `e.g. ${nearbyArenas[0].name}` : 'e.g. Local Sports Ground or Turf'}
 value={playgroundName}
 onChange={e => setPlaygroundName(e.target.value)}
 required
 />
 </div>

 <div className="form-group">
 <label className="form-label">Address / Landmark</label>
 <input
 className="form-input"
 placeholder={defaultAddress ? `e.g. ${defaultAddress}` : 'e.g. Area, Landmark, or Sector'}
 value={address}
 onChange={e => setAddress(e.target.value)}
 />
 </div>

 
 <div className="form-group" style={{ marginBottom: '16px' }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
 <label className="form-label" style={{ margin: 0 }}>Ground Location (GPS)</label>
 <div style={{ display: 'flex', gap: '8px' }}>
 <button
 type="button"
 onClick={handleUseCurrentLocation}
 disabled={isLocating}
 className="location-action-btn"
 title="Detect current location from device"
 >
 {isLocating ? ' Detecting...' : ' Use Current Location'}
 </button>
 <button
 type="button"
 onClick={() => setShowMapPicker(!showMapPicker)}
 className={`location-action-btn ${showMapPicker ? 'active' : ''}`}
 title="Toggle interactive map pin drop"
 >
 {showMapPicker ? 'Hide Map' : 'Pick on Map'}
 </button>
 </div>
 </div>

 
 <div style={{ marginBottom: '10px' }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
 <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
 Popular Arenas:
 </div>
 {districtLabel && (
 <span style={{ fontSize: '10px', color: 'var(--primary)', fontWeight: 600 }}>
 Near {districtLabel}
 </span>
 )}
 </div>
 <div className="venue-chip-container">
 {nearbyArenas.map(v => (
 <button
 type="button"
 key={v.name}
 className={`venue-chip ${playgroundName === v.name ? 'selected' : ''}`}
 onClick={() => {
 setPlaygroundName(v.name);
 setAddress(v.address);
 setLat(v.lat);
 setLng(v.lng);
 setLocationSuccessMsg(`Selected: ${v.name}`);
 }}
 title={`${v.name} • ${v.address}${v.distanceKm ? ` (~${v.distanceKm.toFixed(1)} km away)` : ''}`}
 >
 {v.shortName || v.name}
 </button>
 ))}
 </div>
 </div>

 
 {showMapPicker && (
 <div style={{ marginBottom: '10px' }}>
 <LocationPickerMap
 lat={lat}
 lng={lng}
 onChange={(newLat, newLng) => {
 setLat(newLat);
 setLng(newLng);
 setLocationSuccessMsg(`Pin set: ${newLat}, ${newLng}`);
 }}
 />
 </div>
 )}

 
 <div style={{
 display: 'flex',
 alignItems: 'center',
 justifyContent: 'space-between',
 padding: '8px 12px',
 background: 'var(--elevated)',
 borderRadius: 'var(--radius-md)',
 border: '1px solid var(--border)',
 fontSize: '12px',
 }}>
 <span style={{ color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
 <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--primary)' }} />
 <span>Venue Pin: <strong>Geotag Attached</strong></span>
 </span>
 <a
 href={getGoogleMapsUrl({ name: playgroundName, address, lat, lng })}
 target="_blank"
 rel="noopener noreferrer"
 style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
 title="View location on Google Maps"
 >
 Map <Icons.ExternalLink />
 </a>
 </div>

 {locationSuccessMsg && (
 <div style={{ fontSize: '11px', color: '#C4B5FD', marginTop: '4px' }}>
 <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>{locationSuccessMsg}</span>
 </div>
 )}
 </div>

 <div className="form-row">
 <div className="form-group">
 <label className="form-label">Players Needed</label>
 <input
 className="form-input"
 type="number"
 min={2}
 max={30}
 value={capacity}
 onChange={e => setCapacity(Number(e.target.value))}
 />
 </div>
 <div className="form-group">
 <label className="form-label">Skill Level</label>
 <select
 className="form-input"
 value={skillLevel}
 onChange={e => setSkillLevel(e.target.value)}
 style={{ padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}
 >
 <option value="BEGINNER">Beginner</option>
 <option value="INTERMEDIATE">Intermediate</option>
 <option value="ADVANCED">Advanced</option>
 <option value="MIXED">Mixed</option>
 </select>
 </div>
 </div>

 {/* Calendar UI: 7-Day Window Selection */}
 <div className="form-group" style={{ marginBottom: '16px' }}>
   <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
     <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0, fontWeight: 700 }}>
       <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
         <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
         <line x1="16" y1="2" x2="16" y2="6"/>
         <line x1="8" y1="2" x2="8" y2="6"/>
         <line x1="3" y1="10" x2="21" y2="10"/>
       </svg>
       <span>Event Date</span>
       <span style={{ color: 'var(--primary)', fontSize: '11px', fontWeight: 600 }}>(Next 7 Days Only)</span>
     </label>
     <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
       <input
         type="date"
         className="form-input"
         min={minDateStr}
         max={maxDateStr}
         value={eventDate}
         onChange={e => {
           const val = e.target.value;
           if (val >= minDateStr && val <= maxDateStr) {
             setEventDate(val);
             setErrorMsg('');
           } else {
             setErrorMsg(`Date must be between ${minDateStr} and ${maxDateStr}`);
           }
         }}
         style={{
           padding: '4px 8px',
           fontSize: '12px',
           borderRadius: '6px',
           background: 'var(--surface)',
           border: '1px solid var(--border)',
           color: 'var(--text)',
           width: 'auto',
         }}
       />
     </div>
   </div>

   {/* 7-Day Quick-Pick Pill Strip */}
   <div
     style={{
       display: 'grid',
       gridTemplateColumns: 'repeat(7, 1fr)',
       gap: '6px',
       marginTop: '4px',
     }}
   >
     {calendarDays.map((day) => {
       const isSelected = eventDate === day.dateStr;
       return (
         <button
           key={day.dateStr}
           type="button"
           onClick={() => {
             setEventDate(day.dateStr);
             setErrorMsg('');
           }}
           style={{
             display: 'flex',
             flexDirection: 'column',
             alignItems: 'center',
             justifyContent: 'center',
             padding: '8px 2px',
             borderRadius: '8px',
             border: isSelected
               ? '2px solid var(--primary, #10B981)'
               : '1px solid var(--border)',
             background: isSelected
               ? 'rgba(16, 185, 129, 0.15)'
               : 'var(--elevated, rgba(255, 255, 255, 0.03))',
             cursor: 'pointer',
             transition: 'all 0.15s ease',
             boxShadow: isSelected ? '0 0 10px rgba(16, 185, 129, 0.25)' : 'none',
           }}
         >
           <span
             style={{
               fontSize: '10px',
               fontWeight: 700,
               textTransform: 'uppercase',
               color: isSelected
                 ? 'var(--primary, #10B981)'
                 : day.isToday
                 ? '#38BDF8'
                 : 'var(--text-muted)',
               letterSpacing: '0.2px',
             }}
           >
             {day.weekday}
           </span>
           <span
             style={{
               fontSize: '15px',
               fontWeight: 800,
               lineHeight: '1.2',
               marginTop: '2px',
               color: isSelected ? '#FFFFFF' : 'var(--text)',
             }}
           >
             {day.dayNum}
           </span>
           <span
             style={{
               fontSize: '10px',
               fontWeight: 500,
               color: isSelected ? 'var(--text)' : 'var(--text-secondary)',
             }}
           >
             {day.monthShort}
           </span>
         </button>
       );
     })}
   </div>

   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', fontSize: '11px', color: 'var(--text-secondary)' }}>
     <span>
       Selected: <strong style={{ color: 'var(--text)' }}>{calendarDays.find(d => d.dateStr === eventDate)?.fullLabel || eventDate}</strong>
     </span>
     <span style={{ color: 'var(--primary)', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
       <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
       Within next 7 days only
     </span>
   </div>
 </div>

 <div className="form-row">
 <div className="form-group">
 <label className="form-label">Start Time</label>
 <input
 className="form-input"
 type="time"
 value={startTime}
 onChange={e => setStartTime(e.target.value)}
 />
 </div>
 <div className="form-group">
 <label className="form-label">End Time</label>
 <input
 className="form-input"
 type="time"
 value={endTime}
 onChange={e => setEndTime(e.target.value)}
 />
 </div>
 </div>

 <div className="form-group">
 <label className="form-label">Description (optional)</label>
 <textarea
 className="form-input"
 rows={2}
 placeholder="e.g. Bring your own bat, white tennis ball match..."
 value={description}
 onChange={e => setDescription(e.target.value)}
 style={{ resize: 'vertical', minHeight: '50px' }}
 />
 </div>

 <button
 type="submit"
 className="join-btn"
 disabled={submitting}
 style={{ width: '100%', marginTop: '12px', padding: '12px', fontSize: '15px' }}
 >
 {submitting ? 'Creating Event...' : 'Post Game & Fill Slots'}
 </button>
 </form>
 </div>
 </div>
 );
}
